/**
 * GovernanceArtifacts.tsx  —  Light Enterprise Edition v2
 * ─────────────────────────────────────────────────────────────────────────────
 * NEW in v2:
 *   1. Collapsible FieldNav groups with count badges
 *   2. AI Reasoning tab — decision trace, anon scoring, taxonomy rationale
 *   3. Entity Dashboard modal — KPIs, distribution charts, drill-down filter
 */

import React, { useState, useMemo, useCallback } from "react";
import { adriva_dg_postAction, adriva_exportGovernanceExcel, adriva_dg_getEntityArtifactsBatch } from "../API";
import CatalogPromotionUI from "./CatalogPromotion";
import {
  resolveConfidenceModel, ConfModel,
  AttrConfidenceChip, FieldConfidenceBadge, FieldNavConfidenceDot,
  ClassificationConfidenceSection, TraceChip,
} from "./DGArtifactsConfidence";
import FieldFilterNav from "./DGArtifactsFieldFilterNav";
import ClassificationRulesPanel from "./DGArtifactsClassificationRules";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const T = {
  bg:          "#eef1f6",
  bgCard:      "#ffffff",
  bgSidebar:   "#f5f7fa",
  bgSubbar:    "#ffffff",
  textPrimary: "#0f1923",
  textSecond:  "#1e2d3d",
  textMuted:   "#445566",
  textLabel:   "#2c3e52",
  brand:       "#1e3a6e",
  brandLight:  "#dde7f5",
  brandMid:    "#90aed0",
  brandText:   "#1e3a6e",
  border:      "#c8d4e0",
  borderFocus: "#1e3a6e",
  green:       "#166534",
  greenBg:     "#f0fdf4",
  greenBorder: "#86efac",
  amber:       "#7c2d00",
  amberBg:     "#fff7ed",
  amberBorder: "#fde68a",
  red:         "#991b1b",
  redBg:       "#fef2f2",
  redBorder:   "#fca5a5",
  risk: (s: number) =>
    s >= 4 ? "#991b1b" : s >= 3 ? "#9a3412" : s >= 2 ? "#713f12" : "#166534",
  riskBg: (s: number) =>
    s >= 4 ? "#fef2f2" : s >= 3 ? "#fff7ed" : s >= 2 ? "#fefce8" : "#f0fdf4",
  riskBorder: (s: number) =>
    s >= 4 ? "#fca5a5" : s >= 3 ? "#fdba74" : s >= 2 ? "#fde047" : "#86efac",
  sensitivity: (v: string) => ({
    Restricted:            { color: "#991b1b", bg: "#fef2f2", border: "#fca5a5" },
    "Highly Confidential": { color: "#9a3412", bg: "#fff7ed", border: "#fdba74" },
    Confidential:          { color: "#713f12", bg: "#fefce8", border: "#fde047" },
    Unrestricted:          { color: "#166534", bg: "#f0fdf4", border: "#86efac" },
  }[v] ?? { color: "#1e2d3d", bg: "#f5f7fa", border: "#c8d4e0" }),
};

// ─── TYPES ────────────────────────────────────────────────────────────────────
interface DashFilter { type: string; value: string; }

interface GovernanceUIProps {
  entity_dm?:                any[];
  business_metadata?:        any[];
  metadata_classifications?: any[];
  cde_assessments?:          any[];
  dgConfig?:                 any;
  appNm?:                    string;
  entityId?:                 string;
  lifecycleMap?:             Record<string, Record<string, string>>;
  catalogCreds?:             Record<string, any>;
  catalogMappings?:          Record<string, Record<string, any>>;
  initialFieldFilter?:       string;   // field name to scroll/select on mount
  onFieldFilterClear?:       () => void; // called after field is highlighted
  lazyAppNm?:                string;
  lazyEntityIds?:            string[];
  lazyEntityLabels?:         Record<string, string>;
  /** Optional callback when lazy fetch finishes — useful for parent error UI. */
  onLazyLoaded?:     (count: number, skipped: string[]) => void;
}

// ─── BACKEND ACTION ───────────────────────────────────────────────────────────
function postDgAction(opts: {
  action:     "DG_FIELD_SAVE" | "DG_FIELD_APPROVE";
  entityId:   string; appNm: string; entityKey: string;
  fieldName:  string; data: any; aiSnapshot: any;
}): Promise<{ ok: boolean; msg?: string }> {
  return new Promise((resolve) => {
    const input = JSON.stringify({
      action: opts.action, entity_id: opts.entityId, app_nm: opts.appNm,
      data: { entity_key: opts.entityKey, field_name: opts.fieldName,
              data: opts.data, ai_snapshot: opts.aiSnapshot },
    });
    adriva_dg_postAction(
      input,
      (res: any) => resolve({ ok: res?.success === true, msg: res?.msg }),
      (err: any) => resolve({ ok: false, msg: err?.message ?? "Network error" }),
    );
  });
}

// ─── DATA MERGE ───────────────────────────────────────────────────────────────
function flattenDmFields(node: any): any[] {
  if (!node) return [];
  if (node.eClass === "Field") return [node];
  return (node.field ?? []).flatMap(flattenDmFields);
}

function findParentGroup(root: any, key: string): string {
  // `key` may be:
  //   • FLD_ID  ("Sub-Column")        — new pipeline / FLD_ID-keyed callers
  //   • COLUMN_NAME ("Column")        — legacy callers
  // We match either, so existing call sites that pass COLUMN_NAME keep working.
  function walk(n: any, parentLabel: string): string | null {
    if (n.eClass === "Field") {
      if (n.FLD_ID === key || n.COLUMN_NAME === key) return parentLabel;
      return null;
    }
    for (const c of n.field ?? []) {
      const hit = walk(c, n.label ?? n.name ?? "General");
      if (hit) return hit;
    }
    return null;
  }
  return walk(root, root.label ?? root.name ?? "General") ?? "General";
}

function mergeField(bm: any, fc: any, cde: any, dm: any, entityMeta: any, dmRoot: any): any {
  const fieldName = bm?.field ?? fc?.field ?? dm?.COLUMN_NAME ?? "UNKNOWN";
  //const category  = dm ? findParentGroup(dmRoot, dm.COLUMN_NAME) : bm?.category ?? "General";
  const category = dm
    ? findParentGroup(dmRoot, dm.FLD_ID ?? dm.COLUMN_NAME)
    : (bm?.category ?? "General");

  // fc.classification is authoritative for sensitivity/privacy (Agent 2 output)
  // bm.sensitivity_hint is only a hint — never use it as the display value
  const classification = fc?.classification ?? {
    ownership:             "",
    sensitivity:           "Unknown",
    privacy_category:      "Unknown",
    regulatory_frameworks: [],
  };

  // source_data_type: dm is ground truth (e.g. "STRING", "FLOAT", "INTEGER")
  // bm has wrong/business-mapped values like "Text", "Currency", "Id" — use for catalog_data_type instead
  const sourceDt = dm?.source_data_type ?? bm?.source_data_type ?? "";

  // data_type display: strip "Field" suffix from DM type (TextField→Text, DecimalField→Decimal)
  const rawDmType = dm?.DATA_TYP ?? null;
  const displayDataType = rawDmType
    ? rawDmType.replace(/Field$/, "")
    : null;

  // is_business_mandatory: bm is authoritative, do NOT fall back to dm.MANDATORY
  // dm.MANDATORY="Y" for all fields (it's a technical flag), bm has the real business value
  const isBusinessMandatory =
    bm?.is_business_mandatory !== undefined && bm?.is_business_mandatory !== null
      ? bm.is_business_mandatory
      : bm?.is_required !== undefined
        ? bm.is_required
        : false;   // default false, not true

  // is_business_identifier: same — bm is authoritative
  const isBusinessIdentifier =
    bm?.is_business_identifier !== undefined && bm?.is_business_identifier !== null
      ? bm.is_business_identifier
      : bm?.is_pk !== undefined
        ? bm.is_pk
        : false;

  return {
    field:       fieldName,
    display_name: dm?.ORIG_COLUMN_NAME ?? bm?.business_term ?? fieldName,

    // ── Business metadata (Agent 1) ──────────────────────────────────────────
    business_term:            bm?.business_term ?? bm?.business_element_name ?? dm?.COLUMN_DISPLAY_NAME ?? fieldName,
    definition:               bm?.definition ?? bm?.description ?? dm?.COLUMN_LONG_DESC ?? dm?.COLUMN_SHORT_DESC ?? "",
    data_classification_type: bm?.data_classification_type ?? "",   // was missing entirely
    source_data_type:         sourceDt,                              // dm ground truth
    catalog_data_type:        bm?.catalog_data_type ?? "",           // bm business-mapped type
    business_value_type:      bm?.business_value_type ?? "",
    relates_to_entity:        bm?.relates_to_entity ?? bm?.relates_to ?? "",
    has_relationship:         bm?.has_relationship ?? false,
    related_to:               bm?.related_to ?? "",
    reference_data_candidate: bm?.reference_data_candidate ?? "No",
    reference_data_class:     bm?.reference_data_class ?? "",        // was missing entirely
    is_business_identifier:   isBusinessIdentifier,
    is_business_mandatory:    isBusinessMandatory,
    criticality:              bm?.criticality ?? entityMeta?.business_criticality ?? "Medium",
    steward_role:             bm?.steward_role ?? "",
    usage_contexts:           bm?.usage_contexts ?? entityMeta?.core_usage ?? [],
    lifecycle:                bm?.lifecycle ?? bm?.lifecycle_stages ?? [],
    dq_dimensions:            bm?.dq_dimensions ?? [],
    contains_personal_data:
      bm?.contains_personal_data !== undefined
        ? bm.contains_personal_data
        : fc?.contains_personal_data !== undefined
          ? fc.contains_personal_data
          : ["PII","PHI","Personal Sensitive"].includes(classification.privacy_category ?? ""),
    notes:            bm?.notes ?? "",
    matched_term_id:  bm?.matched_term_id ?? "",   // was missing entirely

    // ── Taxonomy (Agent 1) ───────────────────────────────────────────────────
    taxonomy_id:         bm?.taxonomy_id ?? "TX_PENDING",
    l1_data_category:    bm?.l1_data_category ?? "",
    l2_data_type:        bm?.l2_data_type ?? "",
    l3_data_element:     bm?.l3_data_element ?? "",
    taxonomy_candidates: bm?.taxonomy_candidates ?? [],

    // ── Classification / protection (Agent 2) ────────────────────────────────
    classification,
    data_protection_controls: fc?.data_protection_controls ?? {
      retention_policy: "Unknown", masking_required: false, encryption_required: false,
      access_control: "Internal", policy_justification_summary: "",
    },
    anonymization_assessment: fc?.anonymization_assessment ?? {
      scoring_factors: {}, final_score: 0, score_interpretation: "Not assessed",
    },
    anonymization_decision: fc?.anonymization_decision ?? {
      recommendation: "None", anonymization_required: false, recommended_methods: [],
      reversibility: { reverse_anonymization_allowed: false, reverse_anonymization_required: false, rationale: "" },
      qualitative_considerations: "", override_or_exclusion_rationale: "", confidence: 0,
    },
    decision_trace: fc?.decision_trace ?? null,

    // ── SME-review confidence (Agent 2) ──────────────────────────────────────
    classification_confidence: fc?.classification_confidence ?? {},
    field_confidence:          fc?.field_confidence ?? null,

    // ── CDE assessment (Agent 3) ─────────────────────────────────────────────
    is_cde:           cde?.is_cde           ?? null,
    cde_tier:         cde?.cde_tier         ?? null,
    cde_score:        cde?.cde_score        ?? null,
    cde_confidence:   cde?.cde_confidence   ?? null,
    cde_rationale:    cde?.cde_rationale    ?? "",
    dimension_scores: cde?.dimension_scores ?? null,
    override_allowed: cde?.override_allowed ?? true,
    override_reason:  cde?.override_reason  ?? null,

    // ── DM technical metadata ────────────────────────────────────────────────
    category,
    data_type:           displayDataType,           // "Text", "Decimal" — not "TextField"
    data_length:         dm?.DATA_LEN ?? null,
    short_description:   dm?.COLUMN_SHORT_DESC ?? "",
    long_description:    dm?.COLUMN_LONG_DESC ?? "",
    mandatory_technical: dm?.MANDATORY === "Y",     // technical flag, separate from business mandatory

    // ── AI Reasoning extras ──────────────────────────────────────────────────
    decision_rationale:  bm?.decision_rationale  ?? "",
    confidence_score:    bm?.confidence_score    ?? null,
  };
}

function buildEntityMap(e_dm: any[], bm_arr: any[], mc_arr: any[], cde_arr: any[] = []) {
  const bmMap  = new Map<string,any>(); bm_arr.forEach(b  => bmMap.set((b.entity??"").toLowerCase(), b));
  const mcMap  = new Map<string,any>(); mc_arr.forEach(m  => mcMap.set((m.entity??"").toLowerCase(), m));
  const cdeMap = new Map<string,any>(); cde_arr.forEach(c => cdeMap.set((c.entity??"").toLowerCase(), c));

  return e_dm.map(dmRoot => {
    const key        = (dmRoot.name ?? dmRoot.label ?? "").toLowerCase();
    const bmEntry    = bmMap.get(key) ?? {};
    const mcEntry    = mcMap.get(key) ?? {};
    const cdeEntry   = cdeMap.get(key) ?? {};
    const entityMeta = bmEntry.entity_metadata ?? {};
    const bmFields   = bmEntry.fields ?? [];
    const fcList     = mcEntry.field_classification ?? [];
    const cdeFields  = cdeEntry.fields ?? [];
    const dmLeafs    = flattenDmFields(dmRoot);

    const bmByField  = new Map(bmFields.map((f:any)  => [f.field, f]));
    const fcByField  = new Map(fcList.map((f:any)    => [f.field, f]));
    const cdeByField = new Map(cdeFields.map((f:any) => [f.field, f]));
    /*const dmByName   = new Map(dmLeafs.map((f:any)   => [f.COLUMN_NAME, f]));

    const masterNames: string[] =
      bmFields.length > 0 ? bmFields.map((f:any) => f.field) :
      fcList.length   > 0 ? fcList.map((f:any) => f.field)   :
      dmLeafs.map((f:any) => f.COLUMN_NAME);

    const fields = masterNames.map(name => {
      const dm: any = dmByName.get(name) ??
        dmLeafs.find((f:any) => f.FLD_ID?.endsWith(name) || f.FLD_PHYSICAL_ID?.toLowerCase().includes(name.toLowerCase()));
      return mergeField(bmByField.get(name), fcByField.get(name), cdeByField.get(name), dm, entityMeta, dmRoot);
    });*/

    const dmByFldId = new Map<string, any>();
    const dmByCol   = new Map<string, any>();
    for (const leaf of dmLeafs) {
      if (leaf.FLD_ID)     dmByFldId.set(leaf.FLD_ID, leaf);
      if (leaf.COLUMN_NAME && !dmByCol.has(leaf.COLUMN_NAME)) {
        dmByCol.set(leaf.COLUMN_NAME, leaf);
      }
    }
 
    const masterNames: string[] =
      bmFields.length > 0 ? bmFields.map((f:any) => f.field) :
      fcList.length   > 0 ? fcList.map((f:any) => f.field)   :
      dmLeafs.map((f:any) => f.FLD_ID ?? f.COLUMN_NAME);  // ← also use FLD_ID for the DM-only fallback case
 
    const fields = masterNames.map(name => {
      // Try FLD_ID (new format) → COLUMN_NAME (legacy) → existing endsWith
      // heuristic (very old data). At least one match for normal data.
      const dm: any =
        dmByFldId.get(name) ??
        dmByCol.get(name) ??
        dmLeafs.find((f:any) =>
          f.FLD_ID?.endsWith(name) ||
          f.FLD_PHYSICAL_ID?.toLowerCase().includes(name.toLowerCase()),
        );
      return mergeField(
        bmByField.get(name),
        fcByField.get(name),
        cdeByField.get(name),
        dm,
        entityMeta,
        dmRoot,
      );
    });

    return {
      entityKey: dmRoot.name ?? dmRoot.label ?? "Unknown",
      entityName: dmRoot.label ?? dmRoot.name,
      entityMetadata: entityMeta,
      cdeSummary: cdeEntry.cde_summary ?? null,
      fields,
    };
  });
}

// ─── ATTR PATH MAP ────────────────────────────────────────────────────────────
const ATTR_PATH: Record<string,string> = {
  business_element_name:"business_term",field_name:"field",description:"definition",
  source_data_type:"source_data_type",relates_to:"relates_to_entity",
  business_value_type:"business_value_type",has_relationship:"has_relationship",
  related_to:"related_to",reference_data_candidate:"reference_data_candidate",
  catalog_data_type:"catalog_data_type",data_type_taxonomy:"taxonomy_id",
  l1_data_category_direct:"l1_data_category",l2_data_type_direct:"l2_data_type",
  l3_data_element_direct:"l3_data_element",is_pk:"is_business_identifier",
  is_required:"is_business_mandatory",ownership:"classification.ownership",
  sensitivity:"classification.sensitivity",privacy:"classification.privacy_category",
  regulatory_framework:"classification.regulatory_frameworks",
  anonymization_score:"anonymization_assessment.final_score",
  anonymization_recommendation:"anonymization_decision.recommendation",
  anonymization_required:"anonymization_decision.anonymization_required",
  anonymization_method:"anonymization_decision.recommended_methods",
  reverse_allowed:"anonymization_decision.reversibility.reverse_anonymization_allowed",
  reverse_required:"anonymization_decision.reversibility.reverse_anonymization_required",
  qualitative_factors:"anonymization_decision.qualitative_considerations",
  exclusion_rationale:"anonymization_decision.override_or_exclusion_rationale",
  retention_policy:"data_protection_controls.retention_policy",
  masking_required:"data_protection_controls.masking_required",
  encryption_required:"data_protection_controls.encryption_required",
  access_control:"data_protection_controls.access_control",
  dq_dimensions:"dq_dimensions",steward_role:"steward_role",
  lifecycle_stages:"lifecycle",usage_contexts:"usage_contexts",
  business_criticality:"criticality",is_cde:"is_cde",cde_tier:"cde_tier",
  cde_score:"cde_score",cde_confidence:"cde_confidence",cde_rationale:"cde_rationale",
  override_reason:"override_reason",
};

function getVal(f: any, id: string): any {
  const keys = (ATTR_PATH[id] ?? id).split(".");
  let cur = f; for (const k of keys) { if (cur==null) return undefined; cur=cur[k]; } return cur;
}
function setVal(f: any, id: string, val: any): any {
  const keys = (ATTR_PATH[id] ?? id).split(".");
  const draft = JSON.parse(JSON.stringify(f));
  let cur = draft;
  for (let i=0;i<keys.length-1;i++){if(cur[keys[i]]==null)cur[keys[i]]={};cur[keys[i]]={...cur[keys[i]]};cur=cur[keys[i]];}
  cur[keys[keys.length-1]]=val; return draft;
}

// ─── EXPORT ───────────────────────────────────────────────────────────────────
function doExportCSV(fields:any[], attrs:string[], dgConfig:any) {
  const allAttrs = (dgConfig?.enrichment_groups??[]).flatMap((g:any)=>g.attributes??[]);
  const headers  = ["Field Name",...attrs.map((a:string)=>allAttrs.find((x:any)=>x.id===a)?.label??a)];
  const rows     = fields.map(f=>[f.field,...attrs.map((a:string)=>{const v=getVal(f,a);return Array.isArray(v)?v.join("; "):String(v??"");})]);
  const csv      = [headers,...rows].map(r=>r.map(c=>`"${String(c).replace(/"/g,'""')}"`).join(",")).join("\n");
  const a=document.createElement("a"); a.href=URL.createObjectURL(new Blob([csv],{type:"text/csv"})); a.download="governance_export.csv"; a.click();
}
function doExportJSON(fields:any[], attrs:string[]) {
  const data=fields.map(f=>{const r:any={field_name:f.field};attrs.forEach(a=>{r[a]=getVal(f,a)});return r;});
  const a=document.createElement("a"); a.href=URL.createObjectURL(new Blob([JSON.stringify(data,null,2)],{type:"application/json"})); a.download="governance_export.json"; a.click();
}

function smallBtn(color:string, muted=false): React.CSSProperties {
  return {
    background:"transparent", border:`1px solid ${muted?T.border:color+"60"}`,
    color: muted?T.textMuted:color,
    borderRadius:6,padding:"5px 14px",fontSize:"0.72rem",fontWeight:600,cursor:"pointer",
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// ENTITY TOP BAR
// ─────────────────────────────────────────────────────────────────────────────
const MAX_ENTITY_TABS = 7;

function EntityTopBar({ entities, activeKey, reviewedMap, onSelect, onExport, onPromote, exporting }: {
  entities: { entityKey: string; entityName: string; fields: any[] }[];
  activeKey: string;
  reviewedMap: Map<string, Set<string>>;
  onSelect: (k: string) => void;
  onExport: () => void;
  onPromote: () => void;
  exporting?: boolean;
}) {
  const [overOpen, setOverOpen] = React.useState(false);
  const overRef = React.useRef<HTMLDivElement>(null);

  // Close overflow on outside click
  React.useEffect(() => {
    const h = (e: MouseEvent) => {
      if (overRef.current && !overRef.current.contains(e.target as Node))
        setOverOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const visible  = entities.slice(0, MAX_ENTITY_TABS);
  const overflow = entities.slice(MAX_ENTITY_TABS);
  const overflowHasActive = overflow.some(e => e.entityKey === activeKey);

  const tabInfo = (e: { entityKey: string; entityName: string; fields: any[] }) => {
    const rev   = reviewedMap.get(e.entityKey)?.size ?? 0;
    const total = e.fields.length;
    const pct   = total > 0 ? Math.round((rev / total) * 100) : 0;
    const done  = total > 0 && rev === total;
    return { rev, total, pct, done };
  };

  return (
    <div style={{
      display: "flex", alignItems: "stretch", background: "#fff",
      borderBottom: `2px solid ${T.border}`, flexShrink: 0,
      minHeight: 48, padding: "0 12px", gap: 0,
    }}>

      {/* ── Brand mark ── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        paddingRight: 16, marginRight: 4, flexShrink: 0,
        borderBottom: "3px solid transparent",   // keep height consistent
      }}>
        <div style={{
          width: 28, height: 28, borderRadius: 7, background: T.brand,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 13, color: "#fff", fontWeight: 700, flexShrink: 0,
        }}>⬡</div>
        <div>
          <div style={{ fontSize: "0.68rem", fontWeight: 700, color: T.brand, letterSpacing: "0.06em", textTransform: "uppercase", lineHeight: 1.2 }}>Data Governance</div>
          <div style={{ fontSize: "0.58rem", color: T.textMuted, lineHeight: 1.2 }}>SME Review Console</div>
        </div>
      </div>

      {/* ── Separator ── */}
      <div style={{ width: 1, background: T.border, margin: "10px 4px", flexShrink: 0 }} />

      {/* ── Entity tabs ── */}
      <div style={{ display: "flex", alignItems: "stretch", flex: 1, minWidth: 0, overflow: "visible" }}>
        {visible.map(e => {
          const { rev, total, pct, done } = tabInfo(e);
          const isOn = e.entityKey === activeKey;
          return (
            <button
              key={e.entityKey}
              onClick={() => onSelect(e.entityKey)}
              title={`${e.entityName}  ·  ${rev}/${total} approved`}
              style={{
                all: "unset", boxSizing: "border-box",
                display: "flex", alignItems: "center", gap: 5, padding: "0 14px",
                fontSize: 12, fontWeight: isOn ? 600 : 500, cursor: "pointer",
                whiteSpace: "nowrap", color: isOn ? T.brand : T.textMuted,
                borderBottom: isOn ? `3px solid ${T.green}` : "3px solid transparent",
                transition: "all .14s", maxWidth: 160,
              }}
            >
              {/* Completion dot */}
              <span style={{
                width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
                background: done ? T.green : pct > 0 ? T.brand : T.border,
                transition: "background .2s",
              }} />
              {/* Label */}
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {e.entityName}
              </span>
              {/* Fraction badge — only show when partially done */}
              {total > 0 && rev > 0 && !done && (
                <span style={{
                  fontSize: "0.58rem", fontWeight: 700, flexShrink: 0,
                  color: isOn ? T.brand : T.textMuted,
                  background: isOn ? T.brandLight : T.bg,
                  border: `1px solid ${isOn ? T.brandMid : T.border}`,
                  borderRadius: 99, padding: "0px 5px", lineHeight: 1.6,
                }}>
                  {rev}/{total}
                </span>
              )}
              {done && (
                <span style={{ fontSize: "0.62rem", color: T.green, fontWeight: 700, flexShrink: 0 }}>✓</span>
              )}
            </button>
          );
        })}

        {/* ── Overflow "More ▾" ── */}
        {overflow.length > 0 && (
          <div ref={overRef} style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <button
              onClick={() => setOverOpen(o => !o)}
              style={{
                all: "unset", boxSizing: "border-box",
                display: "flex", alignItems: "center", gap: 5,
                padding: "0 12px", height: "100%",
                fontSize: 12, fontWeight: overflowHasActive ? 600 : 500,
                cursor: "pointer", whiteSpace: "nowrap",
                color: overflowHasActive ? T.brand : T.textMuted,
                borderBottom: overflowHasActive ? `3px solid ${T.green}` : "3px solid transparent",
                transition: "all .14s",
              }}
            >
              {overflowHasActive
                ? <span style={{ maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {overflow.find(e => e.entityKey === activeKey)?.entityName ?? "More"}
                  </span>
                : "More"
              }
              <span style={{
                fontSize: "0.58rem", background: T.brandLight, color: T.brand,
                padding: "1px 6px", borderRadius: 99, fontWeight: 700,
              }}>{overflow.length}</span>
              <span style={{ fontSize: 9 }}>▾</span>
            </button>

            {overOpen && (
              <div style={{
                position: "absolute", top: "calc(100% + 3px)", left: 0, zIndex: 400,
                background: "#fff", border: `1px solid ${T.border}`, borderRadius: 9,
                boxShadow: "0 10px 30px rgba(0,0,0,.12)", minWidth: 200, overflow: "hidden",
              }}>
                {overflow.map(e => {
                  const { rev, total, done } = tabInfo(e);
                  const isOn = e.entityKey === activeKey;
                  return (
                    <div
                      key={e.entityKey}
                      onClick={() => { onSelect(e.entityKey); setOverOpen(false); }}
                      style={{
                        display: "flex", alignItems: "center", gap: 8, padding: "9px 14px",
                        fontSize: 12.5, cursor: "pointer", color: isOn ? T.brand : T.textSecond,
                        background: isOn ? T.brandLight : "transparent", transition: "background .1s",
                        fontWeight: isOn ? 600 : 400,
                      }}
                    >
                      <span style={{
                        width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
                        background: done ? T.green : rev > 0 ? T.brand : T.border,
                      }} />
                      <span style={{ flex: 1 }}>{e.entityName}</span>
                      {total > 0 && (
                        <span style={{ fontSize: "0.62rem", color: T.textMuted, fontFamily: "monospace" }}>
                          {rev}/{total}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Right actions ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 6px", flexShrink: 0 }}>
        <button
          onClick={onExport}
          disabled={exporting}
          style={{
            all: "unset", boxSizing: "border-box",
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "5px 12px", borderRadius: 6, cursor: exporting ? "not-allowed" : "pointer",
            fontSize: 11.5, fontWeight: 500,
            border: `1px solid ${T.border}`, background: "#fff",
            color: exporting ? T.textMuted : T.textSecond,
            opacity: exporting ? 0.6 : 1, transition: "all .12s",
          }}
        >
          {exporting
            ? <><span style={{ display: "inline-block", animation: "dg-spin .8s linear infinite" }}>⟳</span> Exporting…</>
            : <>↓ Export</>
          }
        </button>
        <button
          onClick={onPromote}
          style={{
            all: "unset", boxSizing: "border-box",
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "5px 12px", borderRadius: 6, cursor: "pointer",
            fontSize: 11.5, fontWeight: 500,
            border: `1px solid ${T.brand}`, background: T.brandLight,
            color: T.brand, transition: "all .12s",
          }}
        >
          ↑ Promote
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EXPORT MODAL
// ─────────────────────────────────────────────────────────────────────────────
function ExportModal({fields,dgConfig,onClose}:{fields:any[];dgConfig:any;onClose:()=>void}) {
  const allAttrs = useMemo(()=>
    (dgConfig?.enrichment_groups??[]).flatMap((g:any)=>
      (g.attributes??[]).filter((a:any)=>a.type!=="computed_score")
        .map((a:any)=>({id:a.id,label:a.label,group:g.label,accent:g.accent}))),
  [dgConfig]);
  const [selected,setSelected]=useState<Set<string>>(new Set(allAttrs.map((a:any)=>a.id)));
  const [format,setFormat]=useState<"csv"|"json">("csv");
  const grouped=useMemo(()=>{
    const m=new Map<string,{items:any[];accent:string}>();
    allAttrs.forEach((a:any)=>{const e:{items:any[];accent:string}=m.get(a.group)??{items:[],accent:a.accent};e.items.push(a);m.set(a.group,e)});
    return m;
  },[allAttrs]);
  const toggle=(id:string)=>setSelected(p=>{const n=new Set(p);n.has(id)?n.delete(id):n.add(id);return n;});
  const doExport=()=>{const attrs=[...selected];format==="csv"?doExportCSV(fields,attrs,dgConfig):doExportJSON(fields,attrs);onClose();};
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(26,35,50,0.45)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{background:T.bgCard,borderRadius:14,width:700,maxHeight:"88vh",display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 20px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.06)"}}>
        <div style={{padding:"20px 24px 16px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"flex-start",justifyContent:"space-between"}}>
          <div>
            <div style={{fontSize:"1rem",fontWeight:700,color:T.textPrimary,fontFamily:"'Lora',Georgia,serif"}}>Export Governance Data</div>
            <div style={{fontSize:"0.78rem",color:T.textMuted,marginTop:3}}>{fields.length} field{fields.length!==1?"s":""} · {selected.size} of {allAttrs.length} attributes selected</div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:T.textMuted,fontSize:18,cursor:"pointer",padding:"2px 6px",lineHeight:1}}>✕</button>
        </div>
        <div style={{padding:"14px 24px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:"0.72rem",fontWeight:600,color:T.textLabel,textTransform:"uppercase",letterSpacing:"0.06em",marginRight:4}}>Format</span>
          {(["csv","json"] as const).map(f=>(
            <button key={f} onClick={()=>setFormat(f)} style={{padding:"6px 18px",borderRadius:6,cursor:"pointer",fontWeight:600,fontSize:"0.78rem",textTransform:"uppercase",letterSpacing:"0.05em",background:format===f?T.brand:"transparent",border:format===f?`1px solid ${T.brand}`:`1px solid ${T.border}`,color:format===f?"#fff":T.textSecond,transition:"all 0.15s"}}>{f}</button>
          ))}
          <div style={{marginLeft:"auto",display:"flex",gap:8}}>
            <button onClick={()=>setSelected(new Set(allAttrs.map((a:any)=>a.id)))} style={smallBtn(T.green)}>Select all</button>
            <button onClick={()=>setSelected(new Set())} style={smallBtn(T.red)}>Clear all</button>
          </div>
        </div>
        <div style={{overflowY:"auto",padding:"20px 24px",flexGrow:1}}>
          {[...grouped.entries()].map(([group,{items,accent}])=>(
            <div key={group} style={{marginBottom:22}}>
              <div style={{fontSize:"0.68rem",fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:accent,marginBottom:10,paddingBottom:6,borderBottom:`1px solid ${T.border}`}}>{group}</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"8px 20px"}}>
                {items.map((a:any)=>(
                  <label key={a.id} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
                    <input type="checkbox" checked={selected.has(a.id)} onChange={()=>toggle(a.id)} style={{accentColor:T.brand,width:14,height:14,flexShrink:0}}/>
                    <span style={{fontSize:"0.78rem",color:selected.has(a.id)?T.textPrimary:T.textMuted,fontWeight:selected.has(a.id)?500:400,transition:"color 0.1s"}}>{a.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div style={{padding:"14px 24px",borderTop:`1px solid ${T.border}`,display:"flex",justifyContent:"flex-end",gap:10,background:T.bgSidebar}}>
          <button onClick={onClose} style={smallBtn(T.textMuted,true)}>Cancel</button>
          <button onClick={doExport} style={{background:T.brand,border:"none",color:"#fff",borderRadius:8,padding:"9px 28px",fontSize:"0.82rem",fontWeight:700,cursor:"pointer",boxShadow:"0 2px 8px rgba(44,74,124,0.25)"}}>Download {format.toUpperCase()}</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAXONOMY PICKER  (unchanged)
// ─────────────────────────────────────────────────────────────────────────────
function buildTaxIndex(dgConfig:any) {
  const hier: {id?:string;l1:string;l2:string;l3:string}[] = dgConfig?.taxonomy?.hierarchy ?? [];
  const byId  = new Map<string,{l1:string;l2:string;l3:string}>();
  const byKey = new Map<string,string>();
  hier.forEach((h,i)=>{
    const stableId=h.id; const legacyId=`TX_${i+1}`; const canonicalId=stableId??legacyId;
    const key=`${h.l1}|${h.l2}|${h.l3}`;
    byId.set(canonicalId,h); byKey.set(key,canonicalId);
    if(stableId) byId.set(legacyId,h);
  });
  return {byId,byKey};
}
interface TaxValue { taxonomy_id:string; l1_data_category:string; l2_data_type:string; l3_data_element:string; }

function TaxonomyPicker({taxValue,onChange,dgConfig}:{taxValue:TaxValue;onChange:(v:TaxValue)=>void;dgConfig?:any}) {
  const hier: {l1:string;l2:string;l3:string}[] = dgConfig?.taxonomy?.hierarchy ?? [];
  const {byId,byKey} = useMemo(()=>buildTaxIndex(dgConfig),[dgConfig]);
  const resolved = useMemo(()=>{
    const {taxonomy_id,l1_data_category,l2_data_type,l3_data_element}=taxValue??{};
    if(l1_data_category&&l2_data_type&&l3_data_element) return {l1:l1_data_category,l2:l2_data_type,l3:l3_data_element};
    if(taxonomy_id&&taxonomy_id!=="TX_PENDING"){const hit=byId.get(taxonomy_id);if(hit)return {l1:hit.l1,l2:hit.l2,l3:hit.l3};}
    return null;
  },[taxValue,byId]);
  const [editing,setEditing]=useState(false);
  const [draftL1,setDraftL1]=useState(resolved?.l1??"");
  const [draftL2,setDraftL2]=useState(resolved?.l2??"");
  const [draftL3,setDraftL3]=useState(resolved?.l3??"");
  React.useEffect(()=>{setDraftL1(resolved?.l1??"");setDraftL2(resolved?.l2??"");setDraftL3(resolved?.l3??"");setEditing(false);},[taxValue.taxonomy_id,taxValue.l1_data_category]);
  const l1List=useMemo(()=>[...new Set(hier.map(h=>h.l1))].sort(),[hier]);
  const l2List=useMemo(()=>[...new Set(hier.filter(h=>h.l1===draftL1).map(h=>h.l2))].sort(),[hier,draftL1]);
  const l3List=useMemo(()=>[...new Set(hier.filter(h=>h.l1===draftL1&&h.l2===draftL2).map(h=>h.l3))].sort(),[hier,draftL1,draftL2]);
  const handleL1=(v:string)=>{setDraftL1(v);setDraftL2("");setDraftL3("");};
  const handleL2=(v:string)=>{setDraftL2(v);setDraftL3("");};
  const applySelection=()=>{const key=`${draftL1}|${draftL2}|${draftL3}`;const foundId=byKey.get(key)??taxValue.taxonomy_id??"TX_PENDING";onChange({taxonomy_id:foundId,l1_data_category:draftL1,l2_data_type:draftL2,l3_data_element:draftL3});setEditing(false);};
  const cancelEdit=()=>{setDraftL1(resolved?.l1??"");setDraftL2(resolved?.l2??"");setDraftL3(resolved?.l3??"");setEditing(false);};
  const canApply=!!(draftL1&&draftL2&&draftL3);
  const txId=taxValue?.taxonomy_id; const showId=txId&&txId!=="TX_PENDING";
  if(!editing) return (
    <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
      {resolved?(
        <div style={{display:"flex",alignItems:"stretch",gap:0,border:`1px solid ${T.border}`,borderRadius:9,overflow:"hidden",background:"#fff",flexShrink:0}}>
          <div style={{padding:"7px 14px",background:T.brandLight,borderRight:`1px solid ${T.border}`}}>
            <div style={{fontSize:"0.58rem",fontWeight:700,color:T.brand,letterSpacing:"0.09em",textTransform:"uppercase",marginBottom:2}}>Domain</div>
            <div style={{fontSize:"0.8rem",fontWeight:600,color:T.brand,whiteSpace:"nowrap"}}>{resolved.l1}</div>
          </div>
          <div style={{display:"flex",alignItems:"center",padding:"0 3px",color:T.textMuted,fontSize:"0.8rem",background:"#fff"}}>›</div>
          <div style={{padding:"7px 14px",borderRight:`1px solid ${T.border}`}}>
            <div style={{fontSize:"0.58rem",fontWeight:700,color:T.textLabel,letterSpacing:"0.09em",textTransform:"uppercase",marginBottom:2}}>Category</div>
            <div style={{fontSize:"0.8rem",fontWeight:500,color:T.textPrimary,whiteSpace:"nowrap"}}>{resolved.l2}</div>
          </div>
          <div style={{display:"flex",alignItems:"center",padding:"0 3px",color:T.textMuted,fontSize:"0.8rem",background:"#fff"}}>›</div>
          <div style={{padding:"7px 14px"}}>
            <div style={{fontSize:"0.58rem",fontWeight:700,color:T.textLabel,letterSpacing:"0.09em",textTransform:"uppercase",marginBottom:2}}>Element</div>
            <div style={{fontSize:"0.8rem",fontWeight:600,color:T.textPrimary,whiteSpace:"nowrap"}}>{resolved.l3}</div>
          </div>
          {showId&&(<div style={{display:"flex",alignItems:"center",padding:"0 12px",background:T.bgSidebar,borderLeft:`1px solid ${T.border}`}}><span style={{fontSize:"0.65rem",fontWeight:700,color:T.textMuted,fontFamily:"monospace",letterSpacing:"0.04em"}}>{txId}</span></div>)}
        </div>
      ):(
        <div style={{padding:"9px 16px",border:`1px dashed ${T.border}`,borderRadius:9,fontSize:"0.8rem",color:T.textMuted,background:"#fff",display:"flex",alignItems:"center",gap:8}}>
          {showId?<><span style={{fontFamily:"monospace",fontWeight:700,color:T.textLabel}}>{txId}</span><span style={{color:T.textMuted}}>— hierarchy entry not found in config</span></>:"Not classified — click Edit to assign"}
        </div>
      )}
      <button onClick={()=>setEditing(true)} style={{padding:"7px 16px",borderRadius:7,cursor:"pointer",fontSize:"0.74rem",fontWeight:600,background:T.brandLight,border:`1px solid ${T.brandMid}`,color:T.brand,flexShrink:0,transition:"all 0.12s"}}>✎ Edit</button>
    </div>
  );
  const colBorder=`1px solid ${T.border}`;
  const colHeaderStyle=(active:boolean): React.CSSProperties=>({padding:"8px 12px",background:active?T.brand:T.bgSidebar,borderBottom:colBorder,fontSize:"0.64rem",fontWeight:700,letterSpacing:"0.09em",textTransform:"uppercase",color:active?"#fff":T.textLabel,flexShrink:0});
  const colListStyle: React.CSSProperties={overflowY:"auto",maxHeight:210,padding:"4px 0"};
  const itemStyle=(selected:boolean): React.CSSProperties=>({padding:"7px 14px",cursor:"pointer",fontSize:"0.8rem",fontWeight:selected?600:400,color:selected?T.brand:T.textSecond,background:selected?T.brandLight:"transparent",borderLeft:selected?`3px solid ${T.brand}`:"3px solid transparent",transition:"all 0.1s",userSelect:"none"});
  return (
    <div style={{border:`1px solid ${T.brand}`,borderRadius:10,overflow:"hidden",boxShadow:"0 4px 16px rgba(30,58,110,0.10)"}}>
      <div style={{padding:"10px 16px",borderBottom:colBorder,background:T.bgCard,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <span style={{fontSize:"0.78rem",fontWeight:700,color:T.textPrimary}}>Select Taxonomy Classification</span>
        <div style={{display:"flex",alignItems:"center",gap:5}}>
          {draftL1&&<span style={{fontSize:"0.72rem",fontWeight:600,color:T.brand}}>{draftL1}</span>}
          {draftL2&&<><span style={{color:T.textMuted}}>›</span><span style={{fontSize:"0.72rem",color:T.textSecond,fontWeight:500}}>{draftL2}</span></>}
          {draftL3&&<><span style={{color:T.textMuted}}>›</span><span style={{fontSize:"0.72rem",color:T.textPrimary,fontWeight:700}}>{draftL3}</span></>}
        </div>
      </div>
      <div style={{display:"flex",borderBottom:colBorder,minHeight:170}}>
        <div style={{flex:1,borderRight:colBorder,display:"flex",flexDirection:"column"}}>
          <div style={colHeaderStyle(true)}>L1 — Domain</div>
          <div style={colListStyle}>{l1List.length===0?<div style={{padding:"12px 14px",fontSize:"0.75rem",color:T.textMuted,fontStyle:"italic"}}>No taxonomy in config</div>:l1List.map(l1=><div key={l1} onClick={()=>handleL1(l1)} style={itemStyle(draftL1===l1)}>{l1}</div>)}</div>
        </div>
        <div style={{flex:1,borderRight:colBorder,display:"flex",flexDirection:"column"}}>
          <div style={colHeaderStyle(!!draftL1)}>L2 — Category</div>
          <div style={colListStyle}>{!draftL1?<div style={{padding:"12px 14px",fontSize:"0.75rem",color:T.textMuted,fontStyle:"italic"}}>← Select domain first</div>:l2List.map(l2=><div key={l2} onClick={()=>handleL2(l2)} style={itemStyle(draftL2===l2)}>{l2}</div>)}</div>
        </div>
        <div style={{flex:1,display:"flex",flexDirection:"column"}}>
          <div style={colHeaderStyle(!!draftL2)}>L3 — Element</div>
          <div style={colListStyle}>{!draftL2?<div style={{padding:"12px 14px",fontSize:"0.75rem",color:T.textMuted,fontStyle:"italic"}}>← Select category first</div>:l3List.map(l3=><div key={l3} onClick={()=>setDraftL3(l3)} style={itemStyle(draftL3===l3)}>{l3}</div>)}</div>
        </div>
      </div>
      <div style={{padding:"10px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",background:T.bgCard}}>
        <button onClick={cancelEdit} style={smallBtn(T.textMuted,true)}>Cancel</button>
        <button onClick={applySelection} disabled={!canApply} style={{padding:"8px 22px",borderRadius:7,border:"none",background:canApply?T.brand:"#d0d8e4",color:"#fff",fontSize:"0.78rem",fontWeight:700,cursor:canApply?"pointer":"default",transition:"all 0.15s"}}>Apply Classification</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ATTRIBUTE RENDERER  (unchanged)
// ─────────────────────────────────────────────────────────────────────────────
function AttrRenderer({attr,value,onChange,dgConfig}:{attr:any;value:any;onChange:(v:any)=>void;dgConfig?:any}) {
  const {type,lov,description}=attr;
  const inputBase: React.CSSProperties={width:"100%",background:"#fff",border:`1px solid ${T.border}`,borderRadius:7,padding:"9px 12px",color:T.textPrimary,fontSize:"0.83rem",outline:"none",fontFamily:"inherit",transition:"border-color 0.15s"};
  if(type==="free_text"){const isLong=description?.length>60||attr.id==="description"||attr.id==="qualitative_factors"||attr.id==="exclusion_rationale";return isLong?<textarea value={value??""} onChange={e=>onChange(e.target.value)} rows={3} style={{...inputBase,resize:"vertical",lineHeight:1.55}} placeholder={description}/>:<input type="text" value={value??""} onChange={e=>onChange(e.target.value)} style={inputBase} placeholder={description}/>;}
  if(type==="boolean"){const on=!!value;return(<div onClick={()=>onChange(!on)} style={{display:"inline-flex",alignItems:"center",gap:10,cursor:"pointer",userSelect:"none"}}><div style={{width:42,height:23,borderRadius:12,position:"relative",flexShrink:0,background:on?T.brand:`#d1d9e0`,transition:"background 0.2s"}}><div style={{position:"absolute",top:2.5,left:on?20:2.5,width:18,height:18,borderRadius:"50%",background:"#fff",boxShadow:"0 1px 3px rgba(0,0,0,0.2)",transition:"left 0.2s"}}/></div><span style={{fontSize:"0.82rem",color:on?T.brand:T.textMuted,fontWeight:on?600:400}}>{on?"Yes":"No"}</span></div>);}
  if(type==="lov_single"){const scoreVal=(lov??[]).find((o:any)=>o.id===value)?.score;const desc=(lov??[]).find((o:any)=>o.id===value)?.description;return(<div><div style={{display:"flex",alignItems:"center",gap:8}}><select value={value??""} onChange={e=>onChange(e.target.value)} style={{...inputBase,flex:1,cursor:"pointer",appearance:"none",backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7a90' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,backgroundRepeat:"no-repeat",backgroundPosition:"right 12px center",paddingRight:36}}><option value="">— Select —</option>{(lov??[]).map((o:any)=><option key={o.id} value={o.id}>{o.label}</option>)}</select>{scoreVal!=null&&(<div style={{minWidth:34,height:34,borderRadius:7,flexShrink:0,background:T.riskBg(scoreVal),border:`1px solid ${T.riskBorder(scoreVal)}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"0.78rem",fontWeight:700,color:T.risk(scoreVal)}}>{scoreVal}</div>)}</div>{desc&&value&&(<div style={{fontSize:"0.7rem",color:T.textMuted,marginTop:5,lineHeight:1.4,borderLeft:`2px solid ${T.border}`,paddingLeft:8,marginLeft:1}}>{desc}</div>)}</div>);}
  if(type==="lov_multi"){const arr:string[]=Array.isArray(value)?value:[];return(<div style={{display:"flex",flexWrap:"wrap",gap:6}}>{(lov??[]).map((o:any)=>{const active=arr.includes(o.id);return(<button key={o.id} onClick={()=>onChange(active?arr.filter((x:string)=>x!==o.id):[...arr,o.id])} style={{padding:"5px 13px",borderRadius:20,cursor:"pointer",fontSize:"0.75rem",fontWeight:active?600:400,background:active?T.brandLight:"transparent",border:active?`1px solid ${T.brandMid}`:`1px solid ${T.border}`,color:active?T.brand:T.textSecond,transition:"all 0.12s"}}>{o.label}</button>);})}</div>);}
  if(type==="computed_score"){const n=typeof value==="number"?value:parseFloat(value??0);return(<div style={{display:"inline-flex",alignItems:"center",gap:12,padding:"10px 16px",borderRadius:8,background:T.riskBg(n),border:`1px solid ${T.riskBorder(n)}`}}><span style={{fontSize:"1.8rem",fontWeight:800,color:T.risk(n),lineHeight:1}}>{isNaN(n)?"—":n.toFixed(2)}</span><div><div style={{fontSize:"0.68rem",fontWeight:700,color:T.risk(n),textTransform:"uppercase",letterSpacing:"0.07em"}}>Risk Score</div><div style={{fontSize:"0.68rem",color:T.textMuted,marginTop:1}}>Weighted 0–5</div></div></div>);}
  if(type==="taxonomy"){const taxVal=(value&&typeof value==="object")?value:{taxonomy_id:value??"TX_PENDING",l1_data_category:"",l2_data_type:"",l3_data_element:""};return<TaxonomyPicker taxValue={taxVal} dgConfig={dgConfig} onChange={onChange}/>;}
  return <span style={{fontSize:"0.78rem",color:T.textMuted}}>—</span>;
}

// ─────────────────────────────────────────────────────────────────────────────
// FIELD NAV — FEATURE 1: Collapsible groups with count badges
// ─────────────────────────────────────────────────────────────────────────────
function FieldNav({fields,selectedIdx,approvedSet,onSelect,progress,activeFilter,onClearFilter,model}:{
  fields:any[];selectedIdx:number;approvedSet:Set<string>;
  onSelect:(i:number)=>void;progress:number;
  activeFilter?:{type:string;value:string}|null;
  onClearFilter?:()=>void;
  model:ConfModel;
}) {
  const grouped=useMemo(()=>{
    const m=new Map<string,{f:any;idx:number}[]>();
    fields.forEach((f,idx)=>{const k=f.category??"General";const a=m.get(k)??[];a.push({f,idx});m.set(k,a)});
    return m;
  },[fields]);

  const [collapsed,setCollapsed]=useState<Set<string>>(new Set());
  const toggleGroup=(cat:string)=>setCollapsed(p=>{const n=new Set(p);n.has(cat)?n.delete(cat):n.add(cat);return n;});

  return (
    <div style={{width:248,flexShrink:0,background:T.bgSidebar,borderRight:`1px solid ${T.border}`,display:"flex",flexDirection:"column",overflow:"hidden"}}>
      {/* Progress */}
      <div style={{padding:"14px 16px",borderBottom:`1px solid ${T.border}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <span style={{fontSize:"0.68rem",fontWeight:700,color:T.textLabel,textTransform:"uppercase",letterSpacing:"0.08em"}}>Review Progress</span>
          <span style={{fontSize:"0.78rem",fontWeight:700,color:progress===100?T.green:T.brand}}>{progress}%</span>
        </div>
        <div style={{height:4,background:T.border,borderRadius:99,overflow:"hidden"}}>
          <div style={{width:`${progress}%`,height:"100%",borderRadius:99,background:progress===100?T.green:T.brand,transition:"width 0.4s ease"}}/>
        </div>
        <div style={{fontSize:"0.68rem",color:T.textMuted,marginTop:6}}>{approvedSet.size} of {fields.length} fields approved</div>
      </div>

      {/* Active filter banner */}
      {activeFilter&&(
        <div style={{padding:"8px 12px",background:T.brandLight,borderBottom:`1px solid ${T.brandMid}`,display:"flex",alignItems:"center",justifyContent:"space-between",gap:6}}>
          <div style={{fontSize:"0.67rem",color:T.brand,fontWeight:600,lineHeight:1.3}}>
            <span style={{opacity:0.7}}>Filter: </span>{activeFilter.type}<br/>
            <span style={{fontWeight:700}}>{activeFilter.value}</span>
          </div>
          <button onClick={onClearFilter} style={{background:"none",border:`1px solid ${T.brandMid}`,borderRadius:4,color:T.brand,fontSize:"0.65rem",padding:"2px 7px",cursor:"pointer",flexShrink:0}}>✕ Clear</button>
        </div>
      )}

      {/* Field list */}
      <div style={{overflowY:"auto",flexGrow:1,padding:"4px 0"}}>
        {[...grouped.entries()].map(([cat,items])=>{
          const isCollapsed=collapsed.has(cat);
          const approvedInGroup=items.filter(({f})=>approvedSet.has(f.field)).length;
          const total=items.length;
          const allDone=approvedInGroup===total&&total>0;
          const visibleItems=activeFilter
            ? items.filter(({f})=>{
                if(activeFilter.type==="Sensitivity") return (f.classification?.sensitivity??"")=== activeFilter.value;
                if(activeFilter.type==="CDE Tier")   return f.cde_tier===activeFilter.value;
                if(activeFilter.type==="Steward")    return f.steward_role===activeFilter.value;
                if(activeFilter.type==="Criticality")return f.criticality===activeFilter.value;
                if(activeFilter.type==="Privacy")    return f.contains_personal_data===true;
                if(activeFilter.type==="Pending")    return !approvedSet.has(f.field);
                return true;
              })
            : items;
          if(activeFilter&&visibleItems.length===0) return null;
          return (
            <div key={cat}>
              {/* Collapsible group header */}
              <div onClick={()=>toggleGroup(cat)} style={{
                padding:"9px 14px 7px",display:"flex",alignItems:"center",justifyContent:"space-between",
                cursor:"pointer",
                background:isCollapsed?"#eaecf2":"transparent",
                borderBottom:`1px solid ${isCollapsed?T.border:"transparent"}`,
                transition:"background 0.12s",userSelect:"none",
              }}>
                <div style={{display:"flex",alignItems:"center",gap:6,minWidth:0}}>
                  <span style={{
                    fontSize:"0.6rem",color:T.textMuted,lineHeight:1,flexShrink:0,
                    display:"inline-block",
                    transform:isCollapsed?"rotate(-90deg)":"rotate(0deg)",
                    transition:"transform 0.18s ease",
                  }}>▾</span>
                  <span style={{
                    fontSize:"0.62rem",fontWeight:700,
                    color:allDone?T.green:T.textMuted,
                    letterSpacing:"0.1em",textTransform:"uppercase",
                    overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                  }}>{cat}</span>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:4,flexShrink:0}}>
                  {approvedInGroup>0&&(
                    <span style={{fontSize:"0.58rem",fontWeight:700,color:T.green,background:T.greenBg,border:`1px solid ${T.greenBorder}`,borderRadius:99,padding:"1px 6px",lineHeight:1.4}}>{approvedInGroup}</span>
                  )}
                  <span style={{fontSize:"0.6rem",fontWeight:600,color:T.textMuted,background:T.bg,border:`1px solid ${T.border}`,borderRadius:99,padding:"1px 6px",lineHeight:1.4}}>{total}</span>
                </div>
              </div>
              {/* Fields */}
              {!isCollapsed&&visibleItems.map(({f,idx})=>{
                const isActive=idx===selectedIdx;
                const isApproved=approvedSet.has(f.field);
                const sens=f.classification?.sensitivity??"";
                const sc=T.sensitivity(sens);
                return (
                  <div key={f.field} onClick={()=>onSelect(idx)} style={{
                    padding:"9px 16px 8px 26px",cursor:"pointer",
                    borderLeft:isActive?`3px solid ${T.brand}`:"3px solid transparent",
                    background:isActive?T.brandLight:"transparent",
                    transition:"all 0.12s",
                  }}>
                    <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:6}}>
                      <div style={{minWidth:0}}>
                        <div style={{fontSize:"0.8rem",fontWeight:isActive?600:400,color:isApproved?T.textMuted:isActive?T.brand:T.textPrimary,textDecoration:isApproved?"line-through":"none",lineHeight:1.3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.display_name??f.field}</div>
                        {f.data_type&&(<div style={{fontSize:"0.64rem",color:T.textMuted,marginTop:2,fontFamily:"monospace"}}>{f.data_type}{f.data_length?`(${f.data_length})`:""}</div>)}
                      </div>
                      <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4,flexShrink:0}}>
                        {sens&&<div style={{width:7,height:7,borderRadius:"50%",background:sc.color,flexShrink:0,marginTop:3}}/>}
                        {f.is_cde===true&&(<div title={`CDE · ${f.cde_tier??""}`} style={{fontSize:"0.58rem",fontWeight:800,lineHeight:1,color:f.cde_tier==="Critical"?"#991b1b":"#9a3412",letterSpacing:"0.04em"}}>CDE</div>)}
                        <FieldNavConfidenceDot field={f} model={model}/>
                        {isApproved&&<span style={{fontSize:"0.65rem",color:T.green,fontWeight:700}}>✓</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CDE PANEL  (unchanged)
// ─────────────────────────────────────────────────────────────────────────────
const CDE_DIMS=[
  {key:"d1_regulatory_obligation",label:"D1 · Regulatory Obligation",weight:"30%",desc:"Does this field carry a statutory or regulatory obligation?",method:"rule",methodTip:"Scored deterministically from regulatory_frameworks, sensitivity, privacy_category, and retention_policy."},
  {key:"d2_business_decision_impact",label:"D2 · Business Decision Impact",weight:"25%",desc:"Does this field directly determine a financial result, regulatory filing, or strategic decision?",method:"llm",methodTip:"Scored by AI using business_term, category, taxonomy, usage_contexts, and business_value_type."},
  {key:"d3_dq_consequence",label:"D3 · Data Quality Consequence",weight:"20%",desc:"What is the blast radius if this field contains an error or is missing?",method:"llm",methodTip:"Scored by AI using dq_dimensions, sensitivity, retention_policy, anonymization_score, and regulatory_frameworks."},
  {key:"d4_downstream_breadth",label:"D4 · Downstream Dependency Breadth",weight:"15%",desc:"How widely is this field consumed across systems, reports, and processes?",method:"llm",methodTip:"Scored by AI using has_relationship, reference_data_candidate, usage_contexts count, and relates_to_entity."},
  {key:"d5_identity_integrity",label:"D5 · Identity & Reference Integrity",weight:"10%",desc:"Does this field serve as a key or reference anchor that other data depends on?",method:"rule",methodTip:"Scored deterministically from is_pk, is_business_identifier, business_value_type, and reference_data_candidate."},
];
const CDE_TIER_STYLE: Record<string,{color:string;bg:string;border:string;label:string}> = {
  "Critical":{color:"#991b1b",bg:"#fef2f2",border:"#fca5a5",label:"Critical CDE"},
  "Significant":{color:"#9a3412",bg:"#fff7ed",border:"#fdba74",label:"Significant CDE"},
  "Important":{color:"#713f12",bg:"#fefce8",border:"#fde047",label:"Non-CDE · Important"},
  "Standard":{color:"#166534",bg:"#f0fdf4",border:"#86efac",label:"Non-CDE · Standard"},
};
function CDEScoreBar({score}:{score:number}){const pct=Math.min((score/3)*100,100);const color=score>=3?"#991b1b":score>=2?"#9a3412":score>=1?T.brand:"#c8d4e0";return(<div style={{display:"flex",alignItems:"center",gap:8,flex:1}}><div style={{flex:1,height:6,background:T.border,borderRadius:99,overflow:"hidden"}}><div style={{width:`${pct}%`,height:"100%",background:color,borderRadius:99,transition:"width 0.4s ease"}}/></div><span style={{fontSize:"0.88rem",fontWeight:800,color,minWidth:14,textAlign:"right",fontVariantNumeric:"tabular-nums"}}>{score}</span><span style={{fontSize:"0.68rem",color:T.textMuted,minWidth:18}}>/3</span></div>);}

function CDEPanel({field,handleChange,dgConfig}:{field:any;handleChange:(id:string,v:any)=>void;dgConfig?:any}) {
  const [showOverride,setShowOverride]=useState(false);
  const [overrideDraft,setOverrideDraft]=useState(field.override_reason??"");
  const isCde=field.is_cde;const tier=field.cde_tier??(isCde===null?null:isCde?"Significant":"Standard");
  const score=field.cde_score??null;const confidence=field.cde_confidence??null;
  const dimScores=field.dimension_scores??null;const rationale=field.cde_rationale??"";
  const hasData=isCde!==null&&score!==null;
  const cdeCfg=dgConfig?.cde_scoring??{};const liveWeights=cdeCfg.weights??{};const liveThresh=cdeCfg.thresholds??{};
  if(!hasData)return(<div style={{padding:"28px"}}><div style={{padding:"28px 24px",border:`2px dashed ${T.border}`,borderRadius:12,textAlign:"center",color:T.textMuted}}><div style={{fontSize:"1.8rem",marginBottom:10}}>⏳</div><div style={{fontSize:"0.9rem",fontWeight:600,color:T.textSecond,marginBottom:6}}>CDE Assessment pending</div><div style={{fontSize:"0.78rem",lineHeight:1.6,maxWidth:400,margin:"0 auto"}}>Run the CDE Assessment pipeline (Agent 3) to score this field across 5 dimensions and determine Critical Data Element status.</div></div></div>);
  const tierStyle=CDE_TIER_STYLE[tier??"Standard"]??CDE_TIER_STYLE["Standard"];
  const scorePct=Math.round(((score??0)/3)*100);
  const applyOverride=()=>{handleChange("is_cde",!isCde);handleChange("cde_tier",!isCde?"Significant":"Standard");handleChange("override_reason",overrideDraft);setShowOverride(false);};
  const cancelOverride=()=>{setOverrideDraft(field.override_reason??"");setShowOverride(false);};
  return (
    <div style={{padding:"22px 28px",display:"flex",flexDirection:"column",gap:20}}>
      <div style={{display:"flex",alignItems:"stretch",gap:0,border:`1px solid ${tierStyle.border}`,borderRadius:12,overflow:"hidden",background:tierStyle.bg}}>
        <div style={{padding:"18px 24px",display:"flex",flexDirection:"column",justifyContent:"center",borderRight:`1px solid ${tierStyle.border}`,minWidth:200}}>
          <div style={{fontSize:"0.62rem",fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:tierStyle.color,marginBottom:6}}>CDE Classification</div>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
            <span style={{fontSize:"1.6rem"}}>{isCde?"🔴":"⚪"}</span>
            <div><div style={{fontSize:"1.05rem",fontWeight:800,color:tierStyle.color,lineHeight:1.1}}>{tierStyle.label}</div>{field.override_reason&&<div style={{fontSize:"0.65rem",color:T.amber,fontWeight:600,marginTop:3}}>✎ Steward override applied</div>}</div>
          </div>
          <div style={{display:"inline-flex",alignItems:"center",gap:4,background:"rgba(255,255,255,0.6)",borderRadius:6,padding:"4px 10px",border:`1px solid ${tierStyle.border}`,width:"fit-content"}}><span style={{fontSize:"0.7rem",color:T.textMuted}}>Tier:</span><span style={{fontSize:"0.8rem",fontWeight:700,color:tierStyle.color}}>{tier}</span></div>
        </div>
        <div style={{padding:"18px 28px",display:"flex",flexDirection:"column",justifyContent:"center",alignItems:"center",gap:4,borderRight:`1px solid ${tierStyle.border}`,minWidth:160}}>
          <div style={{fontSize:"0.6rem",fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:T.textMuted,marginBottom:6}}>CDE Score</div>
          <div style={{position:"relative",width:72,height:72}}>
            <svg width="72" height="72" viewBox="0 0 72 72"><circle cx="36" cy="36" r="30" fill="none" stroke={T.border} strokeWidth="7"/><circle cx="36" cy="36" r="30" fill="none" stroke={tierStyle.color} strokeWidth="7" strokeDasharray={`${188.5*(score??0)/3} 188.5`} strokeLinecap="round" transform="rotate(-90 36 36)" style={{transition:"stroke-dasharray 0.6s ease"}}/></svg>
            <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}><span style={{fontSize:"1.2rem",fontWeight:800,color:tierStyle.color,lineHeight:1}}>{(score??0).toFixed(2)}</span><span style={{fontSize:"0.55rem",color:T.textMuted}}>/ 3.00</span></div>
          </div>
          <div style={{fontSize:"0.68rem",color:T.textMuted,marginTop:4}}>{scorePct}th percentile</div>
        </div>
        <div style={{padding:"18px 24px",flex:1,display:"flex",flexDirection:"column",justifyContent:"center",gap:12}}>
          {confidence!==null&&(<div><div style={{fontSize:"0.62rem",fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:T.textMuted,marginBottom:6}}>Assessment Confidence</div><div style={{display:"flex",alignItems:"center",gap:10}}><div style={{flex:1,height:6,background:T.border,borderRadius:99,overflow:"hidden"}}><div style={{width:`${Math.round(confidence*100)}%`,height:"100%",background:confidence>=0.8?T.green:confidence>=0.6?T.brand:T.amber,borderRadius:99,transition:"width 0.4s ease"}}/></div><span style={{fontSize:"0.88rem",fontWeight:700,color:confidence>=0.8?T.green:confidence>=0.6?T.brand:T.amber,minWidth:38,textAlign:"right"}}>{Math.round(confidence*100)}%</span></div><div style={{fontSize:"0.68rem",color:T.textMuted,marginTop:4}}>{confidence>=0.85?"High — signals are explicit and consistent":confidence>=0.70?"Medium — strong inference from available metadata":confidence>=0.50?"Low — some signals ambiguous or missing":"Very low — insufficient metadata for reliable scoring"}</div></div>)}
          <div style={{display:"flex",gap:12}}><div style={{display:"flex",alignItems:"center",gap:6}}><span style={{fontSize:"0.6rem",fontWeight:700,padding:"2px 7px",borderRadius:4,background:"#f0fdf4",border:"1px solid #86efac",color:T.green}}>RULE</span><span style={{fontSize:"0.7rem",color:T.textMuted}}>Deterministic — rule engine</span></div><div style={{display:"flex",alignItems:"center",gap:6}}><span style={{fontSize:"0.6rem",fontWeight:700,padding:"2px 7px",borderRadius:4,background:T.brandLight,border:`1px solid ${T.brandMid}`,color:T.brand}}>AI</span><span style={{fontSize:"0.7rem",color:T.textMuted}}>LLM reasoning with rubric</span></div></div>
        </div>
      </div>
      {dimScores&&(<div style={{border:`1px solid ${T.border}`,borderRadius:12,overflow:"hidden",background:T.bgCard}}><div style={{padding:"12px 20px",borderBottom:`1px solid ${T.border}`,background:T.bgSidebar,display:"flex",alignItems:"center",justifyContent:"space-between"}}><span style={{fontSize:"0.72rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:T.textLabel}}>Dimension Breakdown</span><span style={{fontSize:"0.68rem",color:T.textMuted}}>5 dimensions · max score 3 per dimension</span></div>{CDE_DIMS.map((dim,i)=>{const ds=dimScores[dim.key];const sc=ds?.score??0;const rat=ds?.rationale??"";const meth=ds?.method??dim.method;const isLast=i===CDE_DIMS.length-1;return(<div key={dim.key} style={{padding:"14px 20px",borderBottom:isLast?"none":`1px solid ${T.border}`,background:i%2===0?T.bgCard:"#fafbfd"}}><div style={{display:"flex",alignItems:"center",gap:14,marginBottom:rat?8:0}}><div style={{width:32,height:32,borderRadius:8,flexShrink:0,background:sc>=3?"#fef2f2":sc>=2?"#fff7ed":sc>=1?T.brandLight:T.bg,border:`1px solid ${sc>=3?"#fca5a5":sc>=2?"#fdba74":sc>=1?T.brandMid:T.border}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1rem",fontWeight:800,color:sc>=3?"#991b1b":sc>=2?"#9a3412":sc>=1?T.brand:T.textMuted}}>{sc}</div><div style={{flex:"0 0 auto",minWidth:240}}><div style={{fontSize:"0.8rem",fontWeight:700,color:T.textPrimary}}>{dim.label}</div><div style={{fontSize:"0.66rem",color:T.textMuted,marginTop:1}}>weight: {dim.weight}  ·  contributes {((sc*(ds?.weight||0))).toFixed(3)} to total</div></div><CDEScoreBar score={sc}/><div title={dim.methodTip} style={{fontSize:"0.6rem",fontWeight:700,padding:"3px 8px",borderRadius:4,background:meth==="rule"?"#f0fdf4":T.brandLight,border:meth==="rule"?"1px solid #86efac":`1px solid ${T.brandMid}`,color:meth==="rule"?T.green:T.brand,cursor:"help",flexShrink:0}}>{meth==="rule"?"RULE":"AI"}</div></div>{rat&&<div style={{marginLeft:46,padding:"8px 12px",background:T.bg,borderRadius:6,border:`1px solid ${T.border}`,fontSize:"0.76rem",color:T.textSecond,lineHeight:1.55}}><span style={{fontWeight:600,color:T.textLabel,marginRight:6}}>Rationale:</span>{rat}</div>}<div style={{marginLeft:46,marginTop:6,fontSize:"0.68rem",color:T.textMuted,lineHeight:1.5,fontStyle:"italic"}}>{dim.desc}</div></div>);})}
      </div>)}
      {rationale&&(<div style={{border:`1px solid ${T.border}`,borderRadius:10,overflow:"hidden",background:T.bgCard}}><div style={{padding:"10px 20px",borderBottom:`1px solid ${T.border}`,background:T.bgSidebar,fontSize:"0.72rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:T.textLabel}}>Assessment Summary</div><div style={{padding:"14px 20px",fontSize:"0.8rem",color:T.textSecond,lineHeight:1.65}}>{rationale}</div></div>)}
      {dimScores&&(<div style={{border:`1px solid ${T.border}`,borderRadius:10,background:"#fafbfd",padding:"14px 20px"}}><div style={{fontSize:"0.68rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:T.textLabel,marginBottom:10}}>Weighted Score Calculation</div><div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",fontSize:"0.72rem",color:T.textMuted,lineHeight:2}}>{CDE_DIMS.map((dim,i)=>{const ds=dimScores[dim.key];const sc=ds?.score??0;const wt=ds?.weight??0;return(<React.Fragment key={dim.key}><span style={{padding:"3px 8px",borderRadius:5,background:sc>=2?"#fff7ed":T.bg,border:`1px solid ${sc>=2?"#fdba74":T.border}`,fontFamily:"monospace",fontSize:"0.72rem",fontWeight:600,color:sc>=2?"#9a3412":T.textSecond}}>{sc}×{wt}={(sc*wt).toFixed(3)}</span>{i<CDE_DIMS.length-1&&<span style={{color:T.textMuted,fontWeight:400}}>+</span>}</React.Fragment>);})} <span style={{color:T.textMuted,marginLeft:4}}>=</span><span style={{padding:"3px 10px",borderRadius:5,fontFamily:"monospace",fontWeight:800,fontSize:"0.82rem",background:tierStyle.bg,border:`1px solid ${tierStyle.border}`,color:tierStyle.color,marginLeft:4}}>{(score??0).toFixed(3)}</span><span style={{fontSize:"0.68rem",color:T.textMuted,marginLeft:4}}>→ <strong style={{color:tierStyle.color}}>{tier}</strong>{` (≥ ${liveThresh.cde_significant??1.80} Significant, ≥ ${liveThresh.cde_critical??2.40} Critical)`}</span></div></div>)}
      {Object.keys(liveWeights).length>0&&(<details style={{border:`1px solid ${T.border}`,borderRadius:10,background:"#fafbfd"}}><summary style={{padding:"10px 20px",cursor:"pointer",fontSize:"0.72rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:T.textLabel,listStyle:"none",display:"flex",alignItems:"center",justifyContent:"space-between"}}><span>Policy Configuration  <span style={{fontWeight:400,color:T.textMuted}}>(from dgConfig.cde_scoring)</span></span><span style={{fontSize:"0.8rem",color:T.textMuted}}>▸</span></summary><div style={{padding:"4px 20px 16px",borderTop:`1px solid ${T.border}`}}><div style={{marginTop:14}}><div style={{fontSize:"0.68rem",fontWeight:700,color:T.textLabel,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Dimension Weights</div><div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:8}}>{Object.entries(liveWeights).map(([dim,wt]:any)=>(<div key={dim} style={{padding:"8px 10px",background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:7,textAlign:"center"}}><div style={{fontSize:"0.65rem",fontWeight:700,color:T.textMuted,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>{dim.toUpperCase()}</div><div style={{fontSize:"1rem",fontWeight:800,color:T.brand}}>{(wt*100).toFixed(0)}%</div></div>))}</div></div></div></details>)}
      <div style={{border:`1px solid ${T.border}`,borderRadius:10,overflow:"hidden",background:T.bgCard}}>
        <div style={{padding:"10px 20px",borderBottom:showOverride?`1px solid ${T.border}`:"none",background:T.bgSidebar,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div><span style={{fontSize:"0.72rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:T.textLabel}}>Steward Override</span>{field.override_reason&&(<span style={{marginLeft:10,fontSize:"0.66rem",fontWeight:600,color:T.amber,background:T.amberBg,border:"1px solid #fde68a",borderRadius:4,padding:"2px 8px"}}>Override active</span>)}</div>
          <button onClick={()=>setShowOverride(p=>!p)} style={{background:"transparent",border:`1px solid ${T.border}`,color:T.textSecond,borderRadius:6,padding:"5px 14px",fontSize:"0.72rem",fontWeight:600,cursor:"pointer"}}>{showOverride?"Cancel":"Override CDE Status"}</button>
        </div>
        {showOverride&&(<div style={{padding:"16px 20px"}}><div style={{padding:"10px 14px",background:T.amberBg,border:"1px solid #fde68a",borderRadius:7,fontSize:"0.76rem",color:T.amber,lineHeight:1.55,marginBottom:14}}>⚠ Overriding the AI assessment marks this field as <strong>{isCde?"Not a CDE":"a CDE"}</strong>. This action is logged. Provide a clear business justification below.</div><label style={{fontSize:"0.7rem",fontWeight:700,color:T.textLabel,textTransform:"uppercase",letterSpacing:"0.07em",display:"block",marginBottom:6}}>Business Justification *</label><textarea value={overrideDraft} onChange={e=>setOverrideDraft(e.target.value)} rows={3} placeholder="Explain why the AI classification should be overridden..." style={{width:"100%",padding:"9px 12px",border:`1px solid ${T.border}`,borderRadius:7,fontSize:"0.8rem",color:T.textPrimary,fontFamily:"inherit",resize:"vertical",lineHeight:1.55,background:"#fff"}}/><div style={{display:"flex",gap:8,marginTop:12,justifyContent:"flex-end"}}><button onClick={cancelOverride} style={smallBtn(T.textMuted,true)}>Cancel</button><button onClick={applyOverride} disabled={!overrideDraft.trim()} style={{background:overrideDraft.trim()?T.amber:T.border,border:"none",color:"#fff",borderRadius:7,padding:"8px 22px",fontSize:"0.78rem",fontWeight:700,cursor:overrideDraft.trim()?"pointer":"default",opacity:overrideDraft.trim()?1:0.6}}>Apply Override</button></div></div>)}
        {field.override_reason&&!showOverride&&(<div style={{padding:"12px 20px",display:"flex",alignItems:"flex-start",gap:10}}><span style={{fontSize:"1rem"}}>✎</span><div><div style={{fontSize:"0.7rem",fontWeight:700,color:T.amber,marginBottom:3}}>Steward override reason:</div><div style={{fontSize:"0.78rem",color:T.textSecond,lineHeight:1.55}}>{field.override_reason}</div></div></div>)}
      </div>
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────────────────
// ATTR PANEL — wires AI Reasoning tab
// ─────────────────────────────────────────────────────────────────────────────
function AttrPanel({group,field,handleChange,dgConfig,model}:{group:any;field:any;handleChange:(id:string,v:any)=>void;dgConfig?:any;model:ConfModel}) {
  if(group.id==="cde_assessment") return <CDEPanel field={field} handleChange={handleChange} dgConfig={dgConfig}/>;
  //if(group.id==="ai_reasoning")   return <AIReasoningPanel field={field}/>;
  return (
    <div style={{padding:"22px 28px"}}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"20px 36px"}}>
        {(group.attributes??[]).filter((a:any)=>a.enabled_by_default!==false).map((attr:any)=>{
          const isTaxonomy=attr.type==="taxonomy";
          const val=isTaxonomy?{taxonomy_id:field.taxonomy_id??"TX_PENDING",l1_data_category:field.l1_data_category??"",l2_data_type:field.l2_data_type??"",l3_data_element:field.l3_data_element??""}:getVal(field,attr.id);
          const readOnly=attr.type==="computed_score"||attr.id==="field_name"||attr.id==="business_element_name";
          const fullWidth=isTaxonomy||attr.type==="lov_multi"||(attr.type==="free_text"&&(attr.id==="description"||attr.id==="qualitative_factors"||attr.id==="exclusion_rationale"));
          const handleAttrChange=isTaxonomy?(tv:any)=>{handleChange("data_type_taxonomy",tv.taxonomy_id);handleChange("l1_data_category_direct",tv.l1_data_category);handleChange("l2_data_type_direct",tv.l2_data_type);handleChange("l3_data_element_direct",tv.l3_data_element);}:(v:any)=>handleChange(attr.id,v);
          return (
            <div key={attr.id} style={{gridColumn:fullWidth?"1 / -1":"auto"}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                <label style={{fontSize:"0.7rem",fontWeight:700,color:T.textLabel,textTransform:"uppercase",letterSpacing:"0.07em"}}>{attr.label}</label>
                {attr.mandatory&&<span style={{color:T.red,fontSize:"0.75rem",fontWeight:700,lineHeight:1}}>*</span>}
                <AttrConfidenceChip field={field} attrId={attr.id} model={model} />
                {readOnly&&attr.id!=="business_element_name"&&<span style={{fontSize:"0.6rem",fontWeight:600,color:T.textMuted,background:T.bg,border:`1px solid ${T.border}`,borderRadius:4,padding:"1px 6px"}}>auto</span>}
                {attr.id==="business_element_name"&&(
                  <span style={{fontSize:"0.6rem",fontWeight:700,color:T.brand,background:T.brandLight,border:`1px solid ${T.brandMid}`,borderRadius:4,padding:"1px 8px",display:"inline-flex",alignItems:"center",gap:4}}>
                    📚 Managed in Business Glossary
                  </span>
                )}
              </div>
              {readOnly&&attr.id==="business_element_name"?(
                <div style={{padding:"9px 12px",background:T.brandLight,border:`1px solid ${T.brandMid}`,borderRadius:7,fontSize:"0.84rem",fontWeight:600,color:T.brand,display:"flex",alignItems:"center",gap:10}}>
                  <span style={{flex:1}}>{String(val??"—")}</span>
                  {field.matched_term_id&&(
                    <span style={{fontSize:"0.62rem",fontFamily:"monospace",color:T.textMuted,background:T.bg,border:`1px solid ${T.border}`,borderRadius:4,padding:"2px 6px"}}>{field.matched_term_id}</span>
                  )}
                  <span style={{fontSize:"0.68rem",color:T.textMuted,fontStyle:"italic",fontWeight:400}}>Edit in 📚 Business Glossary page</span>
                </div>
              ):readOnly?(
                <div style={{padding:"9px 12px",background:T.bg,border:`1px solid ${T.border}`,borderRadius:7,fontSize:"0.82rem",color:T.textMuted,fontStyle:"italic"}}>{Array.isArray(val)?val.join(", "):String(val??"—")}</div>
              ):(
                <AttrRenderer attr={attr} value={val} onChange={handleAttrChange} dgConfig={dgConfig}/>
              )}
              {attr.description&&!readOnly&&<div style={{fontSize:"0.68rem",color:T.textMuted,marginTop:4,lineHeight:1.4}}>{attr.description}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FIELD EDITOR — AI Reasoning injected as extra tab
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// AI REASONING MODAL — floats over FieldEditor, triggered from field header
// ─────────────────────────────────────────────────────────────────────────────
function AIReasoningModal({ field, onClose, model }: { field: any; onClose: () => void; model: ConfModel }) {
  const dt        = field.decision_trace;
  const anonAss   = field.anonymization_assessment;
  const anonDec   = field.anonymization_decision;
  const taxCands  = field.taxonomy_candidates ?? [];
  const bizRat    = field.decision_rationale ?? "";
  const confScore = field.confidence_score;
  const notes     = field.notes ?? "";

  const hasAnon = anonAss?.scoring_factors && Object.keys(anonAss.scoring_factors).length > 0;
  const hasDt   = dt && (dt.metadata_signals?.length || dt.rules_triggered?.length || dt.confidence_drivers?.length);
  const hasTax  = taxCands.length > 0;
  const hasBiz  = bizRat || confScore != null;

  const ruleColor = (rule: string) => {
    if (rule.startsWith("PR"))  return { bg: "#fef2f2", color: "#991b1b", border: "#fca5a5" };
    if (rule.startsWith("REG")) return { bg: "#fff7ed", color: "#9a3412", border: "#fdba74" };
    if (rule.startsWith("DP"))  return { bg: T.brandLight, color: T.brand, border: T.brandMid };
    if (rule.startsWith("RET")) return { bg: "#fefce8", color: "#713f12", border: "#fde047" };
    if (rule.startsWith("AN"))  return { bg: "#f0fdf4", color: T.green,   border: T.greenBorder };
    return { bg: T.bg, color: T.textMuted, border: T.border };
  };
  const signalColor = (sig: string) => {
    if (sig.includes("personal_data=true") || sig.includes("sensitivity=High") || sig.includes("Restricted"))
      return { bg: "#fef2f2", color: "#991b1b" };
    if (sig.includes("false") || sig.includes("Low") || sig.includes("Not Applicable"))
      return { bg: "#f0fdf4", color: T.green };
    return { bg: T.bg, color: T.textMuted };
  };

  const ConfBar = ({ value }: { value: number }) => {
    const pct   = Math.round(value * 100);
    const color = value >= 0.8 ? T.green : value >= 0.6 ? T.brand : "#9a3412";
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <div style={{ flex: 1, height: 5, background: T.border, borderRadius: 99, overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 99, transition: "width 0.5s ease" }} />
        </div>
        <span style={{ fontSize: "0.8rem", fontWeight: 700, color, minWidth: 36, textAlign: "right" }}>{pct}%</span>
      </div>
    );
  };

  const ScoreFactor = ({ label, factor }: { label: string; factor: any }) => {
    if (!factor) return null;
    const pct      = Math.round((factor.score / 3) * 100);
    const barColor = factor.score >= 2 ? "#991b1b" : factor.score >= 1 ? T.brand : T.green;
    return (
      <div style={{ padding: "12px 14px", background: T.bg, borderRadius: 8, border: `1px solid ${T.border}` }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 8 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: "0.75rem", fontWeight: 700, color: T.textPrimary }}>{label}</div>
            <div style={{ fontSize: "0.63rem", color: T.textMuted, marginTop: 1 }}>
              weight: {Math.round(factor.weight * 100)}% · contributes <strong style={{ color: T.textLabel }}>{(factor.score * factor.weight).toFixed(3)}</strong>
            </div>
          </div>
          <div style={{
            minWidth: 28, height: 28, borderRadius: 6, flexShrink: 0,
            background: factor.score >= 2 ? "#fef2f2" : factor.score >= 1 ? T.brandLight : T.greenBg,
            border: `1px solid ${factor.score >= 2 ? "#fca5a5" : factor.score >= 1 ? T.brandMid : T.greenBorder}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "0.88rem", fontWeight: 800,
            color: factor.score >= 2 ? "#991b1b" : factor.score >= 1 ? T.brand : T.green,
          }}>{factor.score}</div>
        </div>
        <div style={{ height: 4, background: T.border, borderRadius: 99, overflow: "hidden", marginBottom: 8 }}>
          <div style={{ width: `${pct}%`, height: "100%", background: barColor, borderRadius: 99, transition: "width 0.5s ease" }} />
        </div>
        {factor.justification && (
          <div style={{ fontSize: "0.74rem", color: T.textSecond, lineHeight: 1.55, borderLeft: `2px solid ${T.border}`, paddingLeft: 8 }}>{factor.justification}</div>
        )}
      </div>
    );
  };

  const Sec = ({ icon, title, badge, children }: { icon: string; title: string; badge?: React.ReactNode; children: React.ReactNode }) => (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span style={{ width: 22, height: 22, borderRadius: 5, background: T.brandLight, border: `1px solid ${T.brandMid}`, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", flexShrink: 0 }}>{icon}</span>
        <span style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase" as const, color: T.brand }}>{title}</span>
        {badge && <span style={{ marginLeft: "auto" }}>{badge}</span>}
      </div>
      {children}
    </div>
  );

  const hasConf = field.classification_confidence
    && Object.keys(field.classification_confidence).length > 0;
  const isEmpty = !hasAnon && !hasDt && !hasTax && !hasBiz && !hasConf;

  return (
    // Backdrop — clicking outside closes
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 3000,
        background: "rgba(15,25,35,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 680, maxWidth: "96vw", maxHeight: "88vh",
          background: T.bgCard, borderRadius: 14,
          border: `1px solid ${T.border}`,
          display: "flex", flexDirection: "column", overflow: "hidden",
          boxShadow: "0 24px 64px rgba(0,0,0,0.22), 0 0 0 1px rgba(0,0,0,0.06)",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "16px 20px 14px",
          borderBottom: `1px solid ${T.border}`,
          background: T.brand,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{
              width: 30, height: 30, borderRadius: 8,
              background: "rgba(255,255,255,0.15)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "1rem",
            }}>🤖</span>
            <div>
              <div style={{ fontSize: "0.88rem", fontWeight: 700, color: "#fff" }}>AI Reasoning &amp; Confidence</div>
              <div style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.65)", marginTop: 1 }}>
                {field.display_name ?? field.field} · how ARIA decided each value
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {field.confidence_score != null && (
              <span style={{
                fontSize: "0.7rem", fontWeight: 700,
                padding: "3px 10px", borderRadius: 20,
                background: "rgba(255,255,255,0.18)",
                color: "#fff",
              }}>
                {Math.round(field.confidence_score * 100)}% confidence
              </span>
            )}
            <button onClick={onClose} style={{
              width: 28, height: 28, borderRadius: 6, border: "1px solid rgba(255,255,255,0.25)",
              background: "rgba(255,255,255,0.12)", color: "#fff",
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "0.9rem", lineHeight: 1,
            }}>✕</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", padding: "20px 22px", flexGrow: 1 }}>
          {isEmpty ? (
            <div style={{ padding: "40px 24px", textAlign: "center", color: T.textMuted }}>
              <div style={{ fontSize: "2rem", marginBottom: 12 }}>🤖</div>
              <div style={{ fontSize: "0.9rem", fontWeight: 600, color: T.textSecond, marginBottom: 6 }}>No AI reasoning data available</div>
              <div style={{ fontSize: "0.78rem", lineHeight: 1.65 }}>Run the full pipeline (Agents 1–3) to populate decision traces, scoring rationales, and taxonomy selection reasoning.</div>
            </div>
          ) : (
            <>
              {/* Decision confidence — per-value, config-driven */}
              <ClassificationConfidenceSection field={field} model={model} />

              {/* Business rationale */}
              {hasBiz && (
                <Sec icon="🏷" title="Business classification rationale" badge={
                  confScore != null ? (
                    <span style={{
                      fontSize: "0.65rem", fontWeight: 700, padding: "2px 9px", borderRadius: 5,
                      background: confScore >= 0.8 ? T.greenBg : confScore >= 0.6 ? T.brandLight : "#fff7ed",
                      border: `1px solid ${confScore >= 0.8 ? T.greenBorder : confScore >= 0.6 ? T.brandMid : "#fdba74"}`,
                      color: confScore >= 0.8 ? T.green : confScore >= 0.6 ? T.brand : "#9a3412",
                    }}>Confidence {Math.round(confScore * 100)}%</span>
                  ) : undefined
                }>
                  {confScore != null && <ConfBar value={confScore} />}
                  {bizRat && (
                    <div style={{ padding: "12px 16px", background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, fontSize: "0.8rem", color: T.textSecond, lineHeight: 1.65 }}>{bizRat}</div>
                  )}
                  {notes && (
                    <div style={{ marginTop: 8, padding: "8px 14px", background: T.amberBg, border: `1px solid ${T.amberBorder}`, borderRadius: 7, fontSize: "0.74rem", color: T.amber, lineHeight: 1.55 }}>
                      <span style={{ fontWeight: 700 }}>Note: </span>{notes}
                    </div>
                  )}
                </Sec>
              )}

              {/* Decision trace */}
              {hasDt && (
                <Sec icon="⚙" title="Classification decision trace">
                  <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
                    {dt.metadata_signals?.length > 0 && (
                      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
                        <div style={{ fontSize: "0.62rem", fontWeight: 700, color: T.textLabel, textTransform: "uppercase" as const, letterSpacing: "0.07em", marginBottom: 8 }}>Input signals</div>
                        <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 5 }}>
                          {dt.metadata_signals.map((s: string, i: number) => (
                            <TraceChip key={i} token={s} kind="signal" model={model} />
                          ))}
                        </div>
                      </div>
                    )}
                    {dt.rules_triggered?.length > 0 && (
                      <div style={{ padding: "12px 16px", borderBottom: dt.regulatory_mapping_rationale || dt.data_protection_rationale || dt.anonymization_rationale ? `1px solid ${T.border}` : "none" }}>
                        <div style={{ fontSize: "0.62rem", fontWeight: 700, color: T.textLabel, textTransform: "uppercase" as const, letterSpacing: "0.07em", marginBottom: 8 }}>Rules triggered</div>
                        <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 5 }}>
                          {dt.rules_triggered.map((r: string, i: number) => (
                            <TraceChip key={i} token={r} kind="rule" model={model} />
                          ))}
                        </div>
                      </div>
                    )}
                    {[
                      { key: "regulatory_mapping_rationale", label: "Regulatory mapping" },
                      { key: "data_protection_rationale",    label: "Data protection" },
                      { key: "retention_rationale",          label: "Retention" },
                      { key: "anonymization_rationale",      label: "Anonymization" },
                    ].filter(({ key }) => dt[key]).map(({ key, label }, i, arr) => (
                      <div key={key} style={{ padding: "11px 16px", borderBottom: i < arr.length - 1 ? `1px solid ${T.border}` : "none", background: i % 2 === 0 ? T.bgCard : "#fafbfd" }}>
                        <div style={{ fontSize: "0.62rem", fontWeight: 700, color: T.textLabel, textTransform: "uppercase" as const, letterSpacing: "0.06em", marginBottom: 4 }}>{label}</div>
                        <div style={{ fontSize: "0.77rem", color: T.textSecond, lineHeight: 1.6 }}>{(dt as any)[key]}</div>
                      </div>
                    ))}
                  </div>
                </Sec>
              )}

              {/* Anonymization scoring */}
              {hasAnon && (
                <Sec icon="🔒" title="Anonymization risk scoring" badge={
                  <span style={{ fontSize: "0.65rem", fontWeight: 700, padding: "2px 9px", borderRadius: 5, background: T.bg, border: `1px solid ${T.border}`, color: T.textLabel }}>
                    Score {anonAss.final_score?.toFixed(2) ?? "—"} · {anonAss.score_interpretation ?? ""}
                  </span>
                }>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    {Object.entries(anonAss.scoring_factors).map(([key, factor]: any) => {
                      const label = key === "privacy_impact" ? "Privacy Impact" : key === "sensitivity" ? "Sensitivity" : key === "legal_regulatory" ? "Legal / Regulatory" : key === "ownership_risk" ? "Ownership Risk" : key;
                      return <ScoreFactor key={key} label={label} factor={factor} />;
                    })}
                  </div>
                  {anonDec?.qualitative_considerations && (
                    <div style={{ marginTop: 10, padding: "10px 14px", background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, fontSize: "0.76rem", color: T.textSecond, lineHeight: 1.6 }}>
                      <span style={{ fontWeight: 700, color: T.textLabel, marginRight: 6 }}>Qualitative:</span>{anonDec.qualitative_considerations}
                    </div>
                  )}
                  {anonDec?.override_or_exclusion_rationale && anonDec.override_or_exclusion_rationale !== "None." && (
                    <div style={{ marginTop: 6, padding: "8px 14px", background: T.amberBg, border: `1px solid ${T.amberBorder}`, borderRadius: 7, fontSize: "0.74rem", color: T.amber, lineHeight: 1.55 }}>
                      <span style={{ fontWeight: 700 }}>Exclusion rationale: </span>{anonDec.override_or_exclusion_rationale}
                    </div>
                  )}
                </Sec>
              )}

              {/* Taxonomy candidates */}
              {hasTax && (
                <Sec icon="🌿" title="Taxonomy selection reasoning">
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {taxCands.map((c: any, i: number) => (
                      <div key={c.taxonomy_id} style={{ padding: "12px 16px", background: i === 0 ? T.brandLight : T.bgCard, border: `1px solid ${i === 0 ? T.brandMid : T.border}`, borderRadius: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                          <span style={{ fontSize: "0.65rem", fontWeight: 800, padding: "2px 8px", borderRadius: 4, background: i === 0 ? T.brand : T.bg, border: `1px solid ${i === 0 ? T.brand : T.border}`, color: i === 0 ? "#fff" : T.textMuted, fontFamily: "monospace" }}>{c.taxonomy_id}</span>
                          {i === 0 && <span style={{ fontSize: "0.64rem", fontWeight: 700, color: T.brand }}>★ Selected</span>}
                          <span style={{ marginLeft: "auto", fontSize: "0.65rem", color: T.textMuted }}>Rank {c.rank}</span>
                          {c.taxonomy_confidence != null && <span style={{ fontSize: "0.68rem", fontWeight: 700, color: c.taxonomy_confidence >= 0.7 ? T.green : c.taxonomy_confidence >= 0.5 ? T.brand : "#9a3412" }}>{Math.round(c.taxonomy_confidence * 100)}% conf.</span>}
                        </div>
                        {c.taxonomy_confidence != null && <ConfBar value={c.taxonomy_confidence} />}
                        {c.taxonomy_justification && <div style={{ fontSize: "0.76rem", color: T.textSecond, lineHeight: 1.6 }}>{c.taxonomy_justification}</div>}
                      </div>
                    ))}
                  </div>
                </Sec>
              )}

              {/* Confidence drivers */}
              {dt?.confidence_drivers?.length > 0 && (
                <Sec icon="✦" title="Confidence drivers">
                  <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
                    {dt.confidence_drivers.map((d: string, i: number) => (
                      <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "9px 16px", borderBottom: i < dt.confidence_drivers.length - 1 ? `1px solid ${T.border}` : "none", background: i % 2 === 0 ? T.bgCard : "#fafbfd" }}>
                        <span style={{ color: T.green, fontWeight: 700, fontSize: "0.8rem", flexShrink: 0, marginTop: 1 }}>✓</span>
                        <span style={{ fontSize: "0.76rem", color: T.textSecond, lineHeight: 1.55 }}>{d}</span>
                      </div>
                    ))}
                  </div>
                </Sec>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: "10px 20px",
          borderTop: `1px solid ${T.border}`,
          background: T.bgSidebar,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <span style={{ fontSize: "0.68rem", color: T.textMuted }}>Generated by ARIA · signals from Agents 1–3</span>
          <button onClick={onClose} style={{ ...smallBtn(T.textMuted, true), padding: "5px 16px" }}>Close</button>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// FIELD EDITOR — collapsible sections + AI Reasoning as modal
// ─────────────────────────────────────────────────────────────────────────────
function FieldEditor({field,dgConfig,model,isApproved,isDirty,onUpdate,onApprove,onSave,saveState}:{
  field:any;dgConfig:any;model:ConfModel;isApproved:boolean;isDirty:boolean;
  onUpdate:(f:any)=>void;onApprove:()=>void;onSave:()=>void;
  saveState:"idle"|"saving"|"saved"|"error";
}) {
  const handleChange = (attrId: string, val: any) => onUpdate(setVal(field, attrId, val));
  const groups: any[] = dgConfig?.enrichment_groups ?? [];

  // Section open/close state — all open by default
  const [openSections, setOpenSections] = useState<Set<string>>(() => new Set(groups.map((g: any) => g.id)));
  const toggleSection = (id: string) => setOpenSections(p => {
    const n = new Set(p);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  // AI Reasoning modal
  const [showAI, setShowAI] = useState(false);

  const sens       = field.classification?.sensitivity ?? "";
  const sc         = T.sensitivity(sens);
  const anonScore  = field.anonymization_assessment?.final_score ?? 0;
  const confidence = field.anonymization_decision?.confidence ?? 0;
  const isCde      = field.is_cde;
  const cdeTier    = field.cde_tier;

  // Status pill per group
  const groupStatus = (g: any): { label: string; color: string; bg: string; border: string } => {
    if (g.id === "cde_assessment") {
      if (field.is_cde === null || field.is_cde === undefined)
        return { label: "Pending", color: T.textMuted, bg: T.bg, border: T.border };
      return { label: field.is_cde ? `CDE · ${field.cde_tier ?? ""}` : "Non-CDE", color: field.is_cde ? "#9a3412" : T.green, bg: field.is_cde ? "#fff7ed" : T.greenBg, border: field.is_cde ? "#fdba74" : T.greenBorder };
    }
    const attrs = (g.attributes ?? []).filter((a: any) => a.enabled_by_default !== false);
    const filled = attrs.filter((a: any) => {
      const v = getVal(field, a.id);
      return v !== undefined && v !== null && v !== "" && v !== false && !(Array.isArray(v) && v.length === 0);
    }).length;
    if (filled === 0) return { label: "Empty", color: T.textMuted, bg: T.bg, border: T.border };
    if (filled < attrs.length) return { label: `${filled}/${attrs.length}`, color: T.amber, bg: T.amberBg, border: T.amberBorder };
    return { label: "Complete", color: T.green, bg: T.greenBg, border: T.greenBorder };
  };

  return (
    <div style={{ padding: "20px 28px 36px", margin: "0 auto" }}>
      {/* Field header */}
      <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: "18px 22px", marginBottom: 18, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 20 }}>
          <div style={{ flexGrow: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: "0.67rem", fontWeight: 600, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.07em" }}>{field.category}</span>
              {field.mandatory_technical && <span style={{ fontSize: "0.62rem", fontWeight: 700, color: T.red, background: T.redBg, border: `1px solid ${T.redBorder}`, borderRadius: 4, padding: "1px 7px" }}>Mandatory</span>}
            </div>
            <h2 style={{ margin: 0, fontSize: "1.22rem", fontWeight: 700, color: T.textPrimary, fontFamily: "'Lora',Georgia,serif", letterSpacing: "-0.02em", lineHeight: 1.25 }}>{field.display_name ?? field.field}</h2>
            <div style={{ marginTop: 5, display: "flex", alignItems: "center", gap: 8, fontSize: "0.74rem", color: T.textMuted, fontFamily: "monospace" }}>
              <span>{field.field}</span>
              {field.data_type && <span style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 4, padding: "1px 8px", fontSize: "0.7rem", color: T.textLabel }}>{field.data_type}{field.data_length ? `(${field.data_length})` : ""}</span>}
            </div>
            {field.short_description && <p style={{ margin: "8px 0 0", fontSize: "0.8rem", color: T.textSecond, lineHeight: 1.55, maxWidth: 500 }}>{field.short_description}</p>}
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8, flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {sens && <div style={{ background: sc.bg, border: `1px solid ${sc.border}`, color: sc.color, borderRadius: 6, padding: "4px 12px", fontSize: "0.72rem", fontWeight: 700 }}>{sens}</div>}
              <div style={{ background: T.riskBg(anonScore), border: `1px solid ${T.riskBorder(anonScore)}`, color: T.risk(anonScore), borderRadius: 6, padding: "4px 11px", fontSize: "0.71rem", fontWeight: 700 }}>Risk {anonScore.toFixed(1)}</div>
              {isCde !== null && isCde !== undefined && (
                <div style={{ borderRadius: 6, padding: "4px 11px", fontSize: "0.71rem", fontWeight: 700, background: isCde ? (cdeTier === "Critical" ? "#fef2f2" : "#fff7ed") : "#f0fdf4", border: `1px solid ${isCde ? (cdeTier === "Critical" ? "#fca5a5" : "#fdba74") : "#86efac"}`, color: isCde ? (cdeTier === "Critical" ? "#991b1b" : "#9a3412") : "#166534" }}>
                  {isCde ? `⬤ CDE · ${cdeTier}` : "◯ Non-CDE"}
                </div>
              )}
              {/* Field-level confidence roll-up */}
              <FieldConfidenceBadge field={field} model={model} onClick={() => setShowAI(true)} />
              {/* AI Reasoning trigger — prominent, always visible */}
              <button
                onClick={() => setShowAI(true)}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "4px 12px", borderRadius: 6, cursor: "pointer",
                  background: T.brand, border: `1px solid ${T.brand}`,
                  color: "#fff", fontSize: "0.71rem", fontWeight: 700,
                  transition: "opacity 0.15s",
                }}
              >
                🤖 AI Reasoning
              </button>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <button onClick={onApprove} style={{ background: isApproved ? T.greenBg : "#fff", border: isApproved ? `1px solid ${T.greenBorder}` : `1px solid ${T.border}`, color: isApproved ? T.green : T.textSecond, borderRadius: 7, padding: "7px 16px", fontSize: "0.77rem", fontWeight: 600, cursor: "pointer", transition: "all 0.15s" }}>{isApproved ? "✓ Approved" : "Approve"}</button>
              <button onClick={onSave} disabled={saveState === "saving" || !isDirty} style={{ background: isDirty ? T.brand : "#fff", border: isDirty ? `1px solid ${T.brand}` : `1px solid ${T.border}`, color: isDirty ? "#fff" : T.textMuted, borderRadius: 7, padding: "7px 16px", fontSize: "0.77rem", fontWeight: 600, cursor: isDirty ? "pointer" : "default", transition: "all 0.15s", boxShadow: isDirty ? "0 2px 8px rgba(44,74,124,0.18)" : "none" }}>
                {saveState === "saving" ? "Saving…" : saveState === "saved" ? "✓ Saved" : saveState === "error" ? "✗ Error" : "Save changes"}
              </button>
            </div>
            {isDirty && <div style={{ fontSize: "0.67rem", color: T.amber, fontWeight: 500, marginTop: 2 }}>⚠ Unsaved changes</div>}
          </div>
        </div>
      </div>

      
      {/* Collapsible sections — renders ALL groups including cde_assessment */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {groups.map((g: any) => {
          const isOpen = openSections.has(g.id);
          const status = groupStatus(g);
          return (
            <div key={g.id} style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.03)", transition: "border-color 0.15s" }}>
              <div
                onClick={() => toggleSection(g.id)}
                style={{
                  padding: "13px 20px", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  background: isOpen ? T.bgCard : T.bgSidebar,
                  borderBottom: isOpen ? `1px solid ${T.border}` : "none",
                  borderLeft: `3px solid ${g.id === "cde_assessment" ? "#991b1b" : g.accent ?? T.brand}`,
                  transition: "background 0.12s",
                  userSelect: "none",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: "1rem", lineHeight: 1 }}>{g.icon ?? (g.id === "cde_assessment" ? "📊" : "📋")}</span>
                  <span style={{ fontSize: "0.85rem", fontWeight: 600, color: isOpen ? T.textPrimary : T.textSecond }}>{g.label}</span>
                  {g.subtitle && (
                    <span style={{ fontSize: "0.7rem", color: T.textMuted, fontStyle: "italic", display: isOpen ? "none" : "inline" }}>— {g.subtitle}</span>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{
                    fontSize: "0.65rem", fontWeight: 700, padding: "2px 9px",
                    borderRadius: 20, background: status.bg, border: `1px solid ${status.border}`, color: status.color,
                  }}>{status.label}</span>
                  <span style={{
                    fontSize: "0.65rem", color: T.textMuted,
                    display: "inline-block",
                    transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
                    transition: "transform 0.18s ease",
                  }}>▾</span>
                </div>
              </div>
              {isOpen && (
                <AttrPanel group={g} field={field} handleChange={handleChange} dgConfig={dgConfig} model={model} />
              )}
            </div>
          );
        })}
      </div>

      {/* AI Reasoning modal */}
      {showAI && <AIReasoningModal field={field} onClose={() => setShowAI(false)} model={model} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FEATURE 3: ENTITY DASHBOARD
// ─────────────────────────────────────────────────────────────────────────────
function buildEntityStats(fields: any[], approvedSet: Set<string>) {
  const total=fields.length; const approved=approvedSet.size; const pending=total-approved;
  const sensDist:Record<string,number>={};const cdeDist:Record<string,number>={};
  const stewardDist:Record<string,number>={};const critDist:Record<string,number>={};
  const dqDist:Record<string,number>={};const regDist:Record<string,number>={};
  const lifecycleDist:Record<string,number>={};
  const riskBuckets:Record<string,number>={"Low (0–1)":0,"Medium (1–2)":0,"High (2–3)":0,"Very High (3+)":0};
  let personalDataCount=0;let anonRequiredCount=0;let cdeCount=0;
  for(const f of fields){
    const sens=f.classification?.sensitivity??"Unknown"; sensDist[sens]=(sensDist[sens]??0)+1;
    const tier=f.cde_tier??"Unknown"; cdeDist[tier]=(cdeDist[tier]??0)+1;
    if(f.is_cde===true) cdeCount++;
    const st=f.steward_role||"Unassigned"; stewardDist[st]=(stewardDist[st]??0)+1;
    const cr=f.criticality||"Unknown"; critDist[cr]=(critDist[cr]??0)+1;
    for(const dq of (f.dq_dimensions??[])){ dqDist[dq]=(dqDist[dq]??0)+1; }
    const regs=f.classification?.regulatory_frameworks??[];
    for(const rg of (Array.isArray(regs)?regs:[regs])){ if(rg&&rg!=="Not Applicable"&&rg!=="None"&&rg!=="") regDist[rg]=(regDist[rg]??0)+1; }
    for(const lc of (f.lifecycle??[])){ lifecycleDist[lc]=(lifecycleDist[lc]??0)+1; }
    const rs=f.anonymization_assessment?.final_score??0;
    if(rs>=3) riskBuckets["Very High (3+)"]++; else if(rs>=2) riskBuckets["High (2–3)"]++; else if(rs>=1) riskBuckets["Medium (1–2)"]++; else riskBuckets["Low (0–1)"]++;
    if(f.contains_personal_data===true) personalDataCount++;
    if(f.anonymization_decision?.anonymization_required===true) anonRequiredCount++;
  }
  return {total,approved,pending,cdeCount,personalDataCount,anonRequiredCount,sensDist,cdeDist,stewardDist,critDist,dqDist,regDist,lifecycleDist,riskBuckets};
}

const SENS_ORDER=["Restricted","Highly Confidential","Confidential","Unrestricted","Unknown"];
const SENS_COLORS:Record<string,{color:string;bg:string;border:string}>={
  Restricted:{color:"#991b1b",bg:"#fef2f2",border:"#fca5a5"},
  "Highly Confidential":{color:"#9a3412",bg:"#fff7ed",border:"#fdba74"},
  Confidential:{color:"#713f12",bg:"#fefce8",border:"#fde047"},
  Unrestricted:{color:"#166534",bg:"#f0fdf4",border:"#86efac"},
  Unknown:{color:"#445566",bg:"#f5f7fa",border:"#c8d4e0"},
};
const CRIT_ORDER=["High","Medium","Low","Unknown"];
const CRIT_COLORS:Record<string,string>={High:"#991b1b",Medium:"#9a3412",Low:"#166534",Unknown:"#445566"};

function EntityDashboard({fields,approvedSet,entityName,entityMeta,cdeSummary,onFilter,onClose}:{
  fields:any[];approvedSet:Set<string>;entityName:string;entityMeta:any;cdeSummary:any;
  onFilter:(f:DashFilter)=>void;onClose:()=>void;
}) {
  const s=useMemo(()=>buildEntityStats(fields,approvedSet),[fields,approvedSet]);
  const total=s.total||1;
  const progressPct=s.total>0?Math.round((s.approved/s.total)*100):0;

  function BarRow({label,count,maxCount,color,bg,border,onClick}:{label:string;count:number;maxCount:number;color:string;bg:string;border:string;onClick?:()=>void}){
    const pct=maxCount>0?Math.round((count/maxCount)*100):0;
    return(
      <div onClick={onClick} title={onClick?`Filter by: ${label}`:""}
        style={{display:"flex",alignItems:"center",gap:10,padding:"5px 0",cursor:onClick?"pointer":"default"}}
        onMouseEnter={e=>{if(onClick)(e.currentTarget as HTMLDivElement).style.opacity="0.75";}}
        onMouseLeave={e=>{(e.currentTarget as HTMLDivElement).style.opacity="1";}}>
        <div style={{width:126,flexShrink:0,fontSize:"0.72rem",color:T.textSecond,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textAlign:"right",fontWeight:500}}>{label}</div>
        <div style={{flex:1,height:15,background:T.bg,borderRadius:4,overflow:"hidden",border:`1px solid ${T.border}`,position:"relative"}}>
          <div style={{width:`${pct}%`,height:"100%",background:bg,borderRight:count>0?`2px solid ${color}`:"none",transition:"width 0.5s ease",borderRadius:3}}/>
          {onClick&&count>0&&<div style={{position:"absolute",right:6,top:"50%",transform:"translateY(-50%)",fontSize:"0.55rem",color:color,fontWeight:700,opacity:0.6}}>↑</div>}
        </div>
        <span style={{fontSize:"0.72rem",fontWeight:700,color:count>0?color:T.textMuted,minWidth:22,textAlign:"right"}}>{count}</span>
      </div>
    );
  }

  function KpiCard({label,value,sub,color,bg,border,onClick}:{label:string;value:string|number;sub?:string;color:string;bg:string;border:string;onClick?:()=>void}){
    return(
      <div onClick={onClick} style={{padding:"14px 16px",borderRadius:10,background:bg,border:`1px solid ${border}`,cursor:onClick?"pointer":"default",boxShadow:"0 1px 3px rgba(0,0,0,0.04)",transition:"box-shadow 0.15s"}}
        onMouseEnter={e=>{if(onClick)(e.currentTarget as HTMLDivElement).style.boxShadow="0 4px 12px rgba(0,0,0,0.10)";}}
        onMouseLeave={e=>{(e.currentTarget as HTMLDivElement).style.boxShadow="0 1px 3px rgba(0,0,0,0.04)";}}>
        <div style={{fontSize:"0.6rem",fontWeight:700,color,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>{label}</div>
        <div style={{fontSize:"1.55rem",fontWeight:800,color,lineHeight:1,marginBottom:4}}>{value}</div>
        {sub&&<div style={{fontSize:"0.67rem",color,opacity:0.7}}>{sub}</div>}
        {onClick&&<div style={{fontSize:"0.58rem",color,opacity:0.5,marginTop:4}}>click to filter ↗</div>}
      </div>
    );
  }

  function SectionCard({title,children}:{title:string;children:React.ReactNode}){
    return(
      <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:10,overflow:"hidden",boxShadow:"0 1px 4px rgba(0,0,0,0.04)"}}>
        <div style={{padding:"10px 16px",background:T.bgSidebar,borderBottom:`1px solid ${T.border}`,fontSize:"0.68rem",fontWeight:700,color:T.textLabel,textTransform:"uppercase",letterSpacing:"0.08em"}}>{title}</div>
        <div style={{padding:"14px 16px"}}>{children}</div>
      </div>
    );
  }

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(26,35,50,0.45)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Figtree','Segoe UI',sans-serif"}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Figtree:wght@300;400;500;600;700;800&family=Lora:wght@600;700&display=swap');*{box-sizing:border-box;}::-webkit-scrollbar{width:5px;}::-webkit-scrollbar-thumb{background:#c8d0dc;border-radius:99px;}`}</style>
      <div style={{background:T.bgCard,borderRadius:14,width:"min(1060px,96vw)",maxHeight:"92vh",display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 20px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.06)"}}>
        {/* Header */}
        <div style={{padding:"18px 24px 14px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"flex-start",justifyContent:"space-between",borderTop:`3px solid ${T.brand}`,background:T.bgCard,flexShrink:0}}>
          <div>
            <div style={{fontSize:"0.62rem",fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:T.brand,marginBottom:5}}>Entity Dashboard</div>
            <div style={{fontSize:"1rem",fontWeight:700,color:T.textPrimary,fontFamily:"'Lora',Georgia,serif"}}>{entityName}</div>
            <div style={{fontSize:"0.72rem",color:T.textMuted,marginTop:2}}>
              {entityMeta?.system_of_record&&<span style={{marginRight:12}}>SOR: {entityMeta.system_of_record}</span>}
              {entityMeta?.domain_owner&&<span>Owner: {entityMeta.domain_owner}</span>}
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <span style={{fontSize:"0.7rem",color:T.textMuted,fontStyle:"italic"}}>Click any stat to filter the field list</span>
            <button onClick={onClose} style={{background:"none",border:"none",color:T.textMuted,fontSize:18,cursor:"pointer",padding:"2px 6px",lineHeight:1}}>✕</button>
          </div>
        </div>

        {/* Body */}
        <div style={{overflowY:"auto",padding:"20px 24px",background:T.bg,flexGrow:1}}>
          {/* KPI strip */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:10,marginBottom:16}}>
            <KpiCard label="Total Fields" value={s.total} color={T.brand} bg={T.brandLight} border={T.brandMid}/>
            <KpiCard label="Approved" value={s.approved} sub={`${progressPct}% complete`} color={T.green} bg={T.greenBg} border={T.greenBorder} onClick={()=>{onFilter({type:"Pending",value:"Approved"});onClose();}}/>
            <KpiCard label="Pending" value={s.pending} color="#9a3412" bg={T.amberBg} border={T.amberBorder} onClick={()=>{onFilter({type:"Pending",value:"Pending"});onClose();}}/>
            <KpiCard label="CDE Fields" value={s.cdeCount} sub={cdeSummary?`${cdeSummary.cde_percentage?.toFixed(0)}% of total`:""} color="#991b1b" bg="#fef2f2" border="#fca5a5"/>
            <KpiCard label="Personal Data" value={s.personalDataCount} sub={`${Math.round((s.personalDataCount/total)*100)}% of fields`} color={T.brand} bg={T.brandLight} border={T.brandMid} onClick={()=>{onFilter({type:"Privacy",value:"Personal Data"});onClose();}}/>
            <KpiCard label="Anon. Required" value={s.anonRequiredCount} color="#713f12" bg="#fefce8" border="#fde047"/>
          </div>

          {/* Progress bar */}
          <div style={{background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:10,padding:"14px 18px",marginBottom:16,boxShadow:"0 1px 4px rgba(0,0,0,0.04)"}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
              <span style={{fontSize:"0.7rem",fontWeight:700,color:T.textLabel,textTransform:"uppercase",letterSpacing:"0.07em"}}>Review Progress</span>
              <span style={{fontSize:"0.78rem",fontWeight:700,color:progressPct===100?T.green:T.brand}}>{progressPct}%</span>
            </div>
            <div style={{height:8,background:T.border,borderRadius:99,overflow:"hidden"}}>
              <div style={{width:`${progressPct}%`,height:"100%",borderRadius:99,background:progressPct===100?T.green:T.brand,transition:"width 0.5s ease"}}/>
            </div>
            <div style={{display:"flex",gap:20,marginTop:8}}>
              <span style={{fontSize:"0.68rem",color:T.green,fontWeight:600}}>✓ {s.approved} approved</span>
              <span style={{fontSize:"0.68rem",color:"#9a3412",fontWeight:600}}>⏳ {s.pending} pending</span>
            </div>
          </div>

          {/* Row 2 */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:12}}>
            <SectionCard title="Sensitivity Distribution">
              {SENS_ORDER.filter(k=>s.sensDist[k]>0).map(k=>(<BarRow key={k} label={k} count={s.sensDist[k]??0} maxCount={s.total} color={SENS_COLORS[k].color} bg={SENS_COLORS[k].bg} border={SENS_COLORS[k].border} onClick={()=>{onFilter({type:"Sensitivity",value:k});onClose();}}/>))}
              {Object.keys(s.sensDist).length===0&&<div style={{fontSize:"0.75rem",color:T.textMuted,fontStyle:"italic"}}>No sensitivity data</div>}
            </SectionCard>
            <SectionCard title="CDE Tier Breakdown">
              {["Critical","Significant","Important","Standard","Unknown"].filter(k=>s.cdeDist[k]>0).map(k=>{
                const co=k==="Critical"?"#991b1b":k==="Significant"?"#9a3412":k==="Important"?"#713f12":k==="Standard"?T.green:T.textMuted;
                const bg=k==="Critical"?"#fef2f2":k==="Significant"?"#fff7ed":k==="Important"?"#fefce8":k==="Standard"?T.greenBg:T.bg;
                const br=k==="Critical"?"#fca5a5":k==="Significant"?"#fdba74":k==="Important"?"#fde047":k==="Standard"?T.greenBorder:T.border;
                return <BarRow key={k} label={k} count={s.cdeDist[k]??0} maxCount={s.total} color={co} bg={bg} border={br} onClick={()=>{onFilter({type:"CDE Tier",value:k});onClose();}}/>;
              })}
              <div style={{marginTop:8,paddingTop:8,borderTop:`1px solid ${T.border}`,fontSize:"0.68rem",color:T.textMuted}}>{s.cdeCount} CDE field{s.cdeCount!==1?"s":""} ({Math.round((s.cdeCount/total)*100)}% of total)</div>
            </SectionCard>
            <SectionCard title="Business Criticality">
              {CRIT_ORDER.filter(k=>s.critDist[k]>0).map(k=>(<BarRow key={k} label={k} count={s.critDist[k]??0} maxCount={s.total} color={CRIT_COLORS[k]} bg={k==="High"?"#fef2f2":k==="Medium"?"#fff7ed":k==="Low"?T.greenBg:T.bg} border={k==="High"?"#fca5a5":k==="Medium"?"#fdba74":k==="Low"?T.greenBorder:T.border} onClick={()=>{onFilter({type:"Criticality",value:k});onClose();}}/>))}
            </SectionCard>
          </div>

          {/* Row 3 */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:12}}>
            <SectionCard title="DQ Dimensions Frequency">
              {Object.entries(s.dqDist).sort((a,b)=>b[1]-a[1]).slice(0,8).map(([k,v])=>(<BarRow key={k} label={k} count={v} maxCount={s.total} color={T.brand} bg={T.brandLight} border={T.brandMid}/>))}
              {Object.keys(s.dqDist).length===0&&<div style={{fontSize:"0.75rem",color:T.textMuted,fontStyle:"italic"}}>No DQ dimensions</div>}
            </SectionCard>
            <SectionCard title="Regulatory Coverage">
              {Object.entries(s.regDist).sort((a,b)=>b[1]-a[1]).map(([k,v])=>(<BarRow key={k} label={k} count={v} maxCount={s.total} color="#9a3412" bg="#fff7ed" border="#fdba74"/>))}
              {Object.keys(s.regDist).length===0&&<div style={{fontSize:"0.75rem",color:T.textMuted,fontStyle:"italic"}}>No regulatory frameworks triggered</div>}
            </SectionCard>
            <SectionCard title="Anonymization Risk Distribution">
              {Object.entries(s.riskBuckets).map(([k,v])=>{const co=k.startsWith("Very")||k.startsWith("High")?"#991b1b":k.startsWith("Med")?T.brand:T.green;const bg=k.startsWith("Very")||k.startsWith("High")?"#fef2f2":k.startsWith("Med")?T.brandLight:T.greenBg;const br=k.startsWith("Very")||k.startsWith("High")?"#fca5a5":k.startsWith("Med")?T.brandMid:T.greenBorder;return <BarRow key={k} label={k} count={v} maxCount={s.total} color={co} bg={bg} border={br}/>;})
              }
            </SectionCard>
          </div>

          {/* Row 4 */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <SectionCard title="Steward Role Coverage">
              {Object.entries(s.stewardDist).sort((a,b)=>b[1]-a[1]).map(([k,v])=>(<BarRow key={k} label={k} count={v} maxCount={s.total} color={T.brand} bg={T.brandLight} border={T.brandMid} onClick={()=>{onFilter({type:"Steward",value:k});onClose();}}/>))}
            </SectionCard>
            <SectionCard title="Lifecycle Stage Coverage">
              {Object.entries(s.lifecycleDist).sort((a,b)=>b[1]-a[1]).map(([k,v])=>(<BarRow key={k} label={k} count={v} maxCount={s.total} color={T.green} bg={T.greenBg} border={T.greenBorder}/>))}
              {Object.keys(s.lifecycleDist).length===0&&<div style={{fontSize:"0.75rem",color:T.textMuted,fontStyle:"italic"}}>No lifecycle stages</div>}
            </SectionCard>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ENTITY SHELL — all 3 features wired
// ─────────────────────────────────────────────────────────────────────────────
function EntityShell({entity,dgConfig,appNm,entityId,lifecycleMap,onReviewedChange,initialFieldFilter}:{
  entity:any;dgConfig:any;appNm:string;entityId:string;
  lifecycleMap:Record<string,string>;onReviewedChange:(k:string,r:Set<string>)=>void;
  initialFieldFilter?:string;
}) {
  const [liveFields,    setLiveFields]    = useState<any[]>(entity.fields);
  // SME-driven confidence model, resolved once from the merged dgConfig
  const confModel = useMemo(() => resolveConfidenceModel(dgConfig), [dgConfig]);
  const [approvedSet,   setApprovedSet]   = useState<Set<string>>(() => {
    const s=new Set<string>();
    entity.fields.forEach((f:any)=>{if((lifecycleMap?.[f.field]??"pending")==="approved") s.add(f.field);});
    return s;
  });
  // Jump to field if arriving from crosswalk navigation
  const initialIdx = initialFieldFilter
    ? Math.max(0, entity.fields.findIndex((f:any) => f.field === initialFieldFilter))
    : 0;
  const [selectedIdx,   setSelectedIdx]   = useState(initialIdx);
  const [showExport,    setShowExport]    = useState(false);
  const [showRules,     setShowRules]     = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [dirtySet,      setDirtySet]      = useState<Set<string>>(new Set());
  const [saveStates,    setSaveStates]    = useState<Record<string,"idle"|"saving"|"saved"|"error">>({});
  const [activeFilter,  setActiveFilter]  = useState<DashFilter|null>(null);

  const aiSnapshots=React.useRef<Map<string,any>>(new Map(entity.fields.map((f:any)=>[f.field,JSON.parse(JSON.stringify(f))])));

  // Apply filter: determine which original indices are visible
  const visibleIdxs=useMemo(()=>{
    if(!activeFilter) return liveFields.map((_,i)=>i);
    return liveFields.reduce((acc:number[],f,i)=>{
      let match=false;
      if(activeFilter.type==="Sensitivity") match=(f.classification?.sensitivity??"")=== activeFilter.value;
      else if(activeFilter.type==="CDE Tier") match=f.cde_tier===activeFilter.value;
      else if(activeFilter.type==="Steward") match=f.steward_role===activeFilter.value;
      else if(activeFilter.type==="Criticality") match=f.criticality===activeFilter.value;
      else if(activeFilter.type==="Privacy") match=f.contains_personal_data===true;
      else if(activeFilter.type==="Pending") match=!approvedSet.has(f.field);
      else match=true;
      if(match) acc.push(i);
      return acc;
    },[]);
  },[liveFields,activeFilter,approvedSet]);

  React.useEffect(()=>{
    if(activeFilter && visibleIdxs.length>0 && !visibleIdxs.includes(selectedIdx)) {
      setSelectedIdx(visibleIdxs[0]);
    }
  },[activeFilter]);

  const selectedField=liveFields[selectedIdx];
  const progress=liveFields.length>0?Math.round((approvedSet.size/liveFields.length)*100):0;
  React.useEffect(()=>{onReviewedChange(entity.entityKey,approvedSet);},[approvedSet]);

  const handleUpdate=useCallback((updated:any)=>{
    setLiveFields(p=>p.map(f=>f.field===updated.field?updated:f));
    setDirtySet(p=>{const n=new Set(p);n.add(updated.field);return n;});
  },[]);

  const handleSave=useCallback(async()=>{
    const field=liveFields[selectedIdx]; if(!field) return;
    const fieldName=field.field;
    setSaveStates(p=>({...p,[fieldName]:"saving"}));
    const result=await postDgAction({action:"DG_FIELD_SAVE",entityId,appNm,entityKey:entity.entityKey,fieldName,data:field,aiSnapshot:aiSnapshots.current.get(fieldName)??null});
    setSaveStates(p=>({...p,[fieldName]:result.ok?"saved":"error"}));
    if(result.ok){setDirtySet(p=>{const n=new Set(p);n.delete(fieldName);return n;});aiSnapshots.current.delete(fieldName);setTimeout(()=>setSaveStates(p=>({...p,[fieldName]:"idle"})),2200);}
  },[liveFields,selectedIdx,entityId,appNm,entity.entityKey]);

  const handleApprove=useCallback(async()=>{
    const field=liveFields[selectedIdx]; if(!field) return;
    const fieldName=field.field;const alreadyApproved=approvedSet.has(fieldName);
    if(alreadyApproved){setApprovedSet(p=>{const n=new Set(p);n.delete(fieldName);return n;});return;}
    setSaveStates(p=>({...p,[fieldName]:"saving"}));
    const result=await postDgAction({action:"DG_FIELD_APPROVE",entityId,appNm,entityKey:entity.entityKey,fieldName,data:field,aiSnapshot:aiSnapshots.current.get(fieldName)??null});
    setSaveStates(p=>({...p,[fieldName]:result.ok?"saved":"error"}));
    if(result.ok){
      setApprovedSet(p=>{const n=new Set(p);n.add(fieldName);return n;});
      setDirtySet(p=>{const n=new Set(p);n.delete(fieldName);return n;});
      aiSnapshots.current.delete(fieldName);
      setTimeout(()=>setSaveStates(p=>({...p,[fieldName]:"idle"})),2200);
      const nextIdx=liveFields.findIndex((f,i)=>i>selectedIdx&&!approvedSet.has(f.field)&&f.field!==fieldName);
      if(nextIdx>-1) setTimeout(()=>setSelectedIdx(nextIdx),280);
    }
  },[liveFields,selectedIdx,approvedSet,entityId,appNm,entity.entityKey]);

  return (
    <div style={{display:"flex",flexDirection:"column",flexGrow:1,overflow:"hidden"}}>
      {/* Entity context bar */}
      <div style={{height:44,flexShrink:0,background:T.bgCard,borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 24px"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <span style={{fontSize:"0.72rem",color:T.textMuted}}>Entity:</span>
          <span style={{fontSize:"0.82rem",fontWeight:700,color:T.brand,fontFamily:"'Lora',Georgia,serif"}}>{entity.entityName??entity.entityMetadata.primary_business_entity??entity.entityName}</span>
          {entity.entityMetadata.domain_owner&&<span style={{fontSize:"0.68rem",color:T.textLabel,background:T.bg,border:`1px solid ${T.border}`,borderRadius:5,padding:"2px 9px"}}>{entity.entityMetadata.domain_owner}</span>}
          {entity.entityMetadata.system_of_record&&<span style={{fontSize:"0.68rem",color:T.textMuted,background:T.bg,border:`1px solid ${T.border}`,borderRadius:5,padding:"2px 9px"}}>SOR: {entity.entityMetadata.system_of_record}</span>}
          {entity.cdeSummary&&entity.cdeSummary.total_cde>0&&(<span style={{fontSize:"0.68rem",fontWeight:700,color:"#9a3412",background:"#fff7ed",border:"1px solid #fdba74",borderRadius:5,padding:"2px 9px"}}>⬤ {entity.cdeSummary.total_cde} CDE{entity.cdeSummary.by_tier?.Critical?` · ${entity.cdeSummary.by_tier.Critical} Critical`:""}</span>)}
          {activeFilter&&(
            <div style={{display:"flex",alignItems:"center",gap:5,background:T.brandLight,border:`1px solid ${T.brandMid}`,borderRadius:20,padding:"2px 10px",fontSize:"0.68rem",color:T.brand,fontWeight:600}}>
              <span>{activeFilter.type}: {activeFilter.value}</span>
              <span style={{cursor:"pointer",fontWeight:700,marginLeft:2}} onClick={()=>setActiveFilter(null)}>✕</span>
            </div>
          )}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:"0.72rem",fontWeight:600,color:T.brand,background:T.brandLight,border:`1px solid ${T.brandMid}`,borderRadius:6,padding:"3px 10px"}}>{approvedSet.size}/{liveFields.length} approved</span>
          <button onClick={()=>setShowDashboard(true)} style={{background:"transparent",border:`1px solid ${T.border}`,color:T.textSecond,borderRadius:6,padding:"5px 12px",fontSize:"0.72rem",fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:5,transition:"border-color 0.15s"}}
            onMouseEnter={e=>{(e.currentTarget as HTMLButtonElement).style.borderColor=T.brand;(e.currentTarget as HTMLButtonElement).style.color=T.brand;}}
            onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.borderColor=T.border;(e.currentTarget as HTMLButtonElement).style.color=T.textSecond;}}>
            <span style={{fontSize:11}}>▦</span> Dashboard
          </button>
          <button onClick={()=>setShowRules(true)} title="How ownership, sensitivity, privacy & regulatory values are decided"
            style={{background:"transparent",border:`1px solid ${T.border}`,color:T.textSecond,borderRadius:6,padding:"5px 12px",fontSize:"0.72rem",fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}
            onMouseEnter={e=>{(e.currentTarget as HTMLButtonElement).style.borderColor=T.brand;(e.currentTarget as HTMLButtonElement).style.color=T.brand;}}
            onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.borderColor=T.border;(e.currentTarget as HTMLButtonElement).style.color=T.textSecond;}}>
            <span style={{fontSize:11}}>📖</span> Classification rules
          </button>
          <button onClick={()=>setShowExport(true)} style={{background:"transparent",border:`1px solid ${T.brand}`,color:T.brand,borderRadius:6,padding:"5px 14px",fontSize:"0.74rem",fontWeight:600,cursor:"pointer"}}>↓ Export</button>
        </div>
      </div>

      <div style={{display:"flex",flexGrow:1,overflow:"hidden"}}>
        <FieldFilterNav
          fields={liveFields} selectedIdx={selectedIdx} approvedSet={approvedSet}
          onSelect={setSelectedIdx} progress={progress}
          model={confModel} dgConfig={dgConfig} entityLabel={entity?.entityName}
          activeFilter={activeFilter} onClearFilter={()=>setActiveFilter(null)}
        />
        <div style={{flexGrow:1,overflowY:"auto",background:T.bg}}>
          {selectedField ? (
            <FieldEditor key={selectedField.field} field={selectedField} dgConfig={dgConfig} model={confModel}
              isApproved={approvedSet.has(selectedField.field)} isDirty={dirtySet.has(selectedField.field)}
              onUpdate={handleUpdate} onApprove={handleApprove} onSave={handleSave}
              saveState={saveStates[selectedField.field]??"idle"}/>
          ) : (
            <div style={{height:"100%",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:10,color:T.textMuted,fontSize:"0.88rem"}}>
              {activeFilter
                ? <><div style={{fontSize:"1.4rem"}}>🔍</div><div>No fields match the active filter.</div><button onClick={()=>setActiveFilter(null)} style={smallBtn(T.brand)}>Clear filter</button></>
                : "Select a field from the navigator"}
            </div>
          )}
        </div>
      </div>

      {showExport&&<ExportModal fields={liveFields} dgConfig={dgConfig} onClose={()=>setShowExport(false)}/>}
      {showRules&&<ClassificationRulesPanel dgConfig={dgConfig} onClose={()=>setShowRules(false)}/>}
      {showDashboard&&(
        <EntityDashboard
          fields={liveFields} approvedSet={approvedSet}
          entityName={entity.entityMetadata.primary_business_entity??entity.entityName}
          entityMeta={entity.entityMetadata} cdeSummary={entity.cdeSummary}
          onFilter={f=>setActiveFilter(f)} onClose={()=>setShowDashboard(false)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────────────────────
export default function GovernanceArtifacts({
  entity_dm = [],
  business_metadata = [],
  metadata_classifications = [],
  cde_assessments = [],
  dgConfig,
  appNm = "",
  entityId = "",
  lifecycleMap = {},
  catalogCreds = {},
  catalogMappings = {},
  initialFieldFilter = "",
  onFieldFilterClear,
  lazyAppNm,
  lazyEntityIds,
  lazyEntityLabels,
  onLazyLoaded,
}: GovernanceUIProps) {

  // ── LAZY MODE — per-entity on-demand loading ───────────────────────────────
  const lazyOn = !!(lazyAppNm && lazyEntityIds && lazyEntityIds.length > 0);

  // Same shape as before — all eff_* variables read from this unchanged
  const [lazyData, setLazyData] = useState<{
    entity_dm: any[];
    business_metadata: any[];
    metadata_classifications: any[];
    cde_assessments: any[];
    dg_config: any;
    lifecycle_map: Record<string, Record<string, string>>;
    catalog_creds: Record<string, any>;
    catalog_mappings: Record<string, Record<string, any>>;
  } | null>(null);
  const [lazyError, setLazyError] = useState<string | null>(null);

  // ★ NEW: per-entity tracking
  const [loadedEntityIds, setLoadedEntityIds] = useState<Set<string>>(new Set());
  const [loadingEntityId, setLoadingEntityId] = useState<string | null>(null);

  // lazyLoading = true only when the very first entity is loading (blank screen)
  const lazyLoading = lazyOn && loadingEntityId !== null && loadedEntityIds.size === 0;

  // Load a single entity and MERGE into lazyData
  const loadEntity = useCallback(async (eid: string) => {
    if (!lazyAppNm || loadedEntityIds.has(eid)) return;
    setLoadingEntityId(eid);
    setLazyError(null);
    try {
      const res = await adriva_dg_getEntityArtifactsBatch(lazyAppNm, [eid]);
      if (!res.success) { setLazyError("Failed to load entity"); return; }
      const p = res.data?.[eid];
      if (!p || p.error) { setLazyError(`Entity ${eid} load failed`); return; }

      // Merge this entity's payload into the accumulated lazyData
      setLazyData(prev => {
        const base = prev ?? {
          entity_dm: [], business_metadata: [], metadata_classifications: [],
          cde_assessments: [], dg_config: null, lifecycle_map: {},
          catalog_creds: {}, catalog_mappings: {},
        };

        const lc = { ...base.lifecycle_map };
        const cc = { ...base.catalog_creds };
        const cmap = { ...base.catalog_mappings };

        (p.entity_dm ?? []).forEach((dmRoot: any) => {
          const k = dmRoot.name ?? dmRoot.label ?? "";
          if (k) lc[k] = p.lifecycle_map ?? {};
        });
        if (p.catalog_credentials) Object.assign(cc, p.catalog_credentials);
        if (p.catalog_mapping) {
          (p.entity_dm ?? []).forEach((dmRoot: any) => {
            const k = dmRoot.name ?? dmRoot.label ?? "";
            if (k) cmap[k] = p.catalog_mapping ?? {};
          });
        }

        return {
          entity_dm:                [...base.entity_dm,                ...(p.entity_dm ?? [])],
          business_metadata:        [...base.business_metadata,        ...(p.business_metadata ?? [])],
          metadata_classifications: [...base.metadata_classifications, ...(p.metadata_classifications ?? [])],
          cde_assessments:          [...base.cde_assessments,          ...(p.cde_assessments ?? [])],
          dg_config:                base.dg_config ?? p.dg_config ?? null,
          lifecycle_map:            lc,
          catalog_creds:            cc,
          catalog_mappings:         cmap,
        };
      });

      setLoadedEntityIds(prev => new Set(prev).add(eid));
      onLazyLoaded?.(loadedEntityIds.size + 1, []);
    } catch (e: any) {
      setLazyError(e?.message ?? "Network error");
    } finally {
      setLoadingEntityId(null);
    }
  }, [lazyAppNm, loadedEntityIds, onLazyLoaded]);

  // Load first entity on mount
  React.useEffect(() => {
    if (!lazyOn || !lazyEntityIds?.length) return;
    if (loadedEntityIds.size === 0) loadEntity(lazyEntityIds[0]);
  }, [lazyOn, lazyEntityIds?.[0]]);

  // Reset cache when entity selection changes (user went back to dashboard and picked different set)
  const lazyKey = lazyOn ? [lazyAppNm, ...(lazyEntityIds ?? [])].join("|") : "";
  const prevLazyKeyRef = React.useRef(lazyKey);
  React.useEffect(() => {
    if (prevLazyKeyRef.current !== lazyKey) {
      prevLazyKeyRef.current = lazyKey;
      setLazyData(null);
      setLoadedEntityIds(new Set());
      setLazyError(null);
      if (lazyEntityIds?.length) loadEntity(lazyEntityIds[0]);
    }
  }, [lazyKey]);

  // ── Effective data sources (prop path vs lazy path) ────────────────────────
  const eff_entity_dm                = lazyOn ? (lazyData?.entity_dm                ?? []) : entity_dm;
  const eff_business_metadata        = lazyOn ? (lazyData?.business_metadata        ?? []) : business_metadata;
  const eff_metadata_classifications = lazyOn ? (lazyData?.metadata_classifications ?? []) : metadata_classifications;
  const eff_cde_assessments          = lazyOn ? (lazyData?.cde_assessments          ?? []) : cde_assessments;
  const eff_dgConfig                 = lazyOn ? (lazyData?.dg_config                ?? dgConfig) : dgConfig;
  const eff_lifecycleMap             = lazyOn ? (lazyData?.lifecycle_map            ?? {}) : lifecycleMap;
  const eff_catalogCreds             = lazyOn ? (lazyData?.catalog_creds            ?? {}) : catalogCreds;
  const eff_catalogMappings          = lazyOn ? (lazyData?.catalog_mappings         ?? {}) : catalogMappings;

  // ── All remaining hooks: ALWAYS called, in the same order. ─────────────────
  const entities = useMemo(
    () => buildEntityMap(
      eff_entity_dm,
      eff_business_metadata,
      eff_metadata_classifications,
      eff_cde_assessments,
    ),
    [eff_entity_dm, eff_business_metadata, eff_metadata_classifications, eff_cde_assessments],
  );

  // ★ Map entityId → entityKey (entity name) for loaded entities
  const entityIdToKey = useMemo(() => {
    if (!lazyOn) return new Map<string, string>();
    const m = new Map<string, string>();
    (lazyData?.entity_dm ?? []).forEach((dm: any) => {
      const eid = dm.entityId;
      const key = dm.name ?? dm.label ?? "";
      if (eid && key) m.set(eid, key);
    });
    return m;
  }, [lazyOn, lazyData?.entity_dm]);

  // ★ Placeholder entities for unloaded tabs (keeps tab bar complete)
  const entitiesWithPlaceholders = useMemo(() => {
    if (!lazyOn || !lazyEntityIds) return entities;
    const placeholders = lazyEntityIds
      .filter(eid => !loadedEntityIds.has(eid))
      .map(eid => ({
        entityKey:      eid,
        entityName:     lazyEntityLabels?.[eid] ?? eid,
        entityMetadata: {},
        cdeSummary:     null,
        fields:         [] as any[],
        _placeholder:   true,
      }));
    return [...entities, ...placeholders];
  }, [entities, lazyOn, lazyEntityIds, loadedEntityIds, lazyEntityLabels]);

  const [activeKey,     setActiveKey]     = useState<string>("");
  const [reviewedMap,   setReviewedMap]   = useState<Map<string, Set<string>>>(new Map());
  const [showPromotion, setShowPromotion] = useState(false);
  const [exporting,     setExporting]     = useState(false);
  const [pendingFieldFilter, setPendingFieldFilter] = useState<string>("");

  // Make sure activeKey is always valid for the current `entities`
  // Make sure activeKey is always valid
  React.useEffect(() => {
    if (entitiesWithPlaceholders.length === 0) {
      if (activeKey !== "") setActiveKey("");
      return;
    }
    // When a placeholder loads, its entityKey (=entityId) gets replaced by entityName
    // Transition activeKey from entityId → entityName
    if (lazyOn && entityIdToKey.has(activeKey)) {
      setActiveKey(entityIdToKey.get(activeKey)!);
      return;
    }
    if (!entitiesWithPlaceholders.find(e => e.entityKey === activeKey)) {
      setActiveKey(entitiesWithPlaceholders[0].entityKey);
    }
  }, [entitiesWithPlaceholders, activeKey, entityIdToKey, lazyOn]);

  const promotionEntities = useMemo(
    () => entities.map(e => ({ entityKey: e.entityKey, entityName: e.entityName })),
    [entities],
  );

  // Field-filter (cross-page navigation from BusinessGlossaryPage)
  React.useEffect(() => {
    if (!initialFieldFilter) return;
    const ownerEntity = entities.find(e =>
      e.fields.some((f: any) => f.field === initialFieldFilter),
    );
    if (ownerEntity) {
      setActiveKey(ownerEntity.entityKey);
      setPendingFieldFilter(initialFieldFilter);
    }
    const t = setTimeout(() => {
      setPendingFieldFilter("");
      onFieldFilterClear?.();
    }, 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFieldFilter, entities]);

  // ── Plain (non-hook) helpers and derived values ────────────────────────────
  const handleReviewedChange = (key: string, reviewed: Set<string>) =>
    setReviewedMap(p => { const n = new Map(p); n.set(key, reviewed); return n; });

  const activeEntity = entities.find(e => e.entityKey === activeKey);

  const handleExcelExport = async () => {
    setExporting(true);
    try {
      const payload = entities.map(e => ({
        entity_key:      e.entityKey,
        entity_name:     e.entityName,
        entity_metadata: e.entityMetadata,
        fields:          e.fields,
      }));
      await adriva_exportGovernanceExcel(appNm || lazyAppNm || "", entityId, payload);
    } catch (err) {
      console.error("Excel export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  // ── Render — early-return branches below are OK now: all hooks above ───────
  const baseShellStyle: React.CSSProperties = {
    height: "1130px",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    fontFamily: "'Figtree','Segoe UI',sans-serif",
    background: T.bg,
  };

  if (lazyOn && lazyLoading && !lazyData) {
    return (
      <div style={{
        ...baseShellStyle,
        alignItems: "center", justifyContent: "center",
        flexDirection: "row", color: T.textMuted,
      }}>
        <span style={{ marginRight: 10, fontSize: "1.4rem", animation: "dg-spin 1s linear infinite" }}>⟳</span>
        <span style={{ fontSize: "0.92rem" }}>
          Loading governance artifacts for {lazyEntityIds?.length} entit{(lazyEntityIds?.length ?? 0) === 1 ? "y" : "ies"}…
        </span>
        <style>{`@keyframes dg-spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }`}</style>
      </div>
    );
  }

  if (lazyOn && lazyError) {
    return (
      <div style={baseShellStyle}>
        <div style={{
          margin: 24, padding: "14px 18px",
          background: T.redBg, border: `1px solid ${T.redBorder}`,
          borderRadius: 8, color: T.red, fontSize: "0.84rem",
        }}>
          Failed to load governance artifacts: {lazyError}
        </div>
      </div>
    );
  }

  if (entities.length === 0) {
    return (
      <div style={{
        ...baseShellStyle,
        alignItems: "center", justifyContent: "center",
        flexDirection: "row", color: T.textMuted, fontSize: "0.9rem",
      }}>
        No entity data provided.
      </div>
    );
  }

  return (
    <div style={baseShellStyle}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Figtree:wght@300;400;500;600;700;800&family=Lora:wght@600;700&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        ::-webkit-scrollbar{width:5px;height:5px;}
        ::-webkit-scrollbar-track{background:transparent;}
        ::-webkit-scrollbar-thumb{background:#c8d0dc;border-radius:99px;}
        ::-webkit-scrollbar-thumb:hover{background:#a0aab8;}
        input:focus,select:focus,textarea:focus{outline:none!important;border-color:${T.brand}!important;box-shadow:0 0 0 3px ${T.brand}18!important;}
        button:hover{opacity:0.88;}
        select option{background:#fff;color:${T.textPrimary};}
        @keyframes dg-spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }
      `}</style>

      <EntityTopBar
        entities={entitiesWithPlaceholders}
        activeKey={activeKey}
        reviewedMap={reviewedMap}
        onSelect={(key) => {
          setActiveKey(key);
          // ★ If clicking an unloaded placeholder tab, trigger load
          if (lazyOn && lazyEntityIds?.includes(key) && !loadedEntityIds.has(key)) {
            loadEntity(key);
          }
        }}
        onExport={handleExcelExport}
        onPromote={() => setShowPromotion(true)}
        exporting={exporting}
      />

      {showPromotion && (
        <CatalogPromotionUI
          entities={promotionEntities}
          appNm={appNm || lazyAppNm || ""}
          entityId={entityId}
          savedCreds={eff_catalogCreds ?? {}}
          savedMappings={eff_catalogMappings ?? {}}
          onClose={() => setShowPromotion(false)}
        />
      )}

      {activeEntity ? (
        <EntityShell
          key={activeEntity.entityKey}
          entity={activeEntity}
          dgConfig={eff_dgConfig}
          appNm={appNm || lazyAppNm || ""}
          entityId={entityId}
          lifecycleMap={eff_lifecycleMap[activeEntity.entityKey] ?? {}}
          onReviewedChange={handleReviewedChange}
          initialFieldFilter={pendingFieldFilter || undefined}
        />
      ) : loadingEntityId ? (
        <div style={{
          flexGrow: 1, display: "flex", alignItems: "center",
          justifyContent: "center", flexDirection: "column",
          color: T.textMuted, gap: 12,
        }}>
          <span style={{ fontSize: "1.4rem", animation: "dg-spin 1s linear infinite" }}>⟳</span>
          <span style={{ fontSize: "0.88rem" }}>Loading entity data…</span>
          <span style={{ fontSize: "0.72rem", color: T.textMuted }}>
            {loadedEntityIds.size} of {lazyEntityIds?.length ?? 0} entities loaded
          </span>
        </div>
      ) : (
        <div style={{
          flexGrow: 1, display: "flex", alignItems: "center",
          justifyContent: "center", color: T.textMuted,
        }}>
          Select an entity above to begin.
        </div>
      )}
    </div>
  );
}